import { TitleImages } from '../backbone/features/mobileForm/BaseField/views';
import { ImageFieldView } from '../backbone/features/mobileForm/imageField/views';
import { MobileFormStructure } from '../backbone/features/mobileForm/MobileFormStructure/models';
import { CommentsPageLayoutMixin } from '../backbone/shared/models/CommentsPageLayoutMixin';
import { EmptyListMessage } from '../backbone/shared/views/EmptyListMessage';
import { BaseToolbarNavPage, BaseToolbarNavPageLayout, EmptyView } from '../backbone/shared/views/baseLayoutViews';
import { MobileSearchBar } from '../backbone/shared/views/MobileSearchBar';
import { SelectAccountModal } from '../backbone/shared/views/SelectAccountModal';
import { BaseTabbedLayoutPage, TabbedLayout } from '../backbone/shared/views/tabs';
import { BaseTypeaheadModal, MobileBaseTypeahead } from '../backbone/shared/views/typeaheads';
import {
    AccountsAndDistributeGroups,
    AccountsForTaskBuckets,
    MobileUserRoles,
    ProjectSearchOptions,
    ProjectTaskSearchOptions,
    TabbedLayoutPageList,
    TaskSearchOptions
} from './models';
import { authorizeDjangosaml2 } from './djangosaml2';
import {
    Account,
    AccountTags,
    DistributeGroups,
    FormTemplate,
    FormTemplates,
    global_utils,
    Image,
    Images,
    Paginator,
    User,
    UserNotificationSettings,
    UserRoles,
    UsersDistributeGroupsAndRoles
} from './shared/forms';
import { BatchTask, MediaFieldTask, MobileProject, MobileProjects, MobileTasks, MyTasks } from './tasks/models';
import localization from './utils/localization';
import { getNotificationSettingsTranslation } from './utils/notificationSettings';
import { PrivacyPolicy } from '../backbone/features/PrivacyPolicy';
import { getSSOCompanyInfo } from '../../../js/shared/sso';
import { isForethoughtAvailable, showForethoughtWidget } from './utils/logging/forethought';
import {
    SUPPORT_CHAT_WIDGET_KEY,
    SUPPORT_CHAT_ZENDESK_VALUE,
    getLocalStorageWithExpiry
} from './utils/logging/supportChatUtils';
import { showZendeskWidget } from './utils/logging/zendesk';
import { setStatusBarColor } from './utils/setStatusBarColor';
import { CordovaPlugins } from '../shared/CordovaPlugins';
import { downloadApp } from '../shared/utils/version';
import { isAndroid, isConnected, isIos, isNative, isWeb, isWindows } from '../shared/utils/is';
import {
    actionSheet,
    confirmNotification,
    hideLoadingDialog,
    showOfflineMessage,
    showSuccessMessage,
    showLoadingDialog,
    showErrorMessage
} from '../shared/utils/notifications';
import {
    disableDebugging,
    enableDebugging,
    enableWeinreRemoteDebug,
    getDebuggingFlag,
    isDebuggingEnabled,
    isReleaseBuild,
    setDebuggingFlag
} from '../shared/utils/debugging';

/**
 * These are shared functions for views
 */

export const NotificationSettingsItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_notification_settings_item',
    tagName: 'li',
    className: 'form-field',

    events: {
        'change select': 'save_value'
    },

    initialize: function () {
        this.model.set('name', getNotificationSettingsTranslation(this.model.get('translation_id')));
        NotificationSettingsItem.__super__.initialize.apply(this, arguments);
    },

    templateHelpers: function () {
        return {
            DEPLOY_ENV: process.env.DEPLOY_ENV,
            push_notifications_enabled: this.model.collection.push_notifications_enabled,
            sms_notifications_enabled: this.model.collection.sms_notifications_enabled
        };
    },

    save_value: function (event) {
        var target = $(event.target);
        this.model.set('delivery_type', target.val());
    }
});

export const Settings = BaseToolbarNavPage.extend({
    template: '#tpl_settings_list',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_my_setttings');
    },
    is_top_level_page: true,

    events: {
        'click #profile_settings': 'click_profile',
        'click #notification_settings': 'click_notification',
        'click #devices_settings': 'click_devices_settings',
        'click #privacy_policy': 'click_privacy_policy',
        'click #remote_debug': 'remote_debug'
    },

    templateHelpers: function () {
        return {
            version_info: $f.mobile.get_version_string()
        };
    },

    initialize: function () {
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_settings');
    },

    click_profile: function (event) {
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        $f.mobile.nav.show(MobileUserProfileSettings, { use_transitions: true });
        return false;
    },

    click_notification: function (event) {
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        $f.mobile.nav.show(NotificationSettings, { use_transitions: true });
        return false;
    },

    click_devices_settings: function (event) {
        $f.mobile.nav.show(Devices, { use_transitions: true });
        return false;
    },

    click_privacy_policy: function (event) {
        $f.mobile.nav.show(PrivacyPolicy, { use_transitions: true });
        return false;
    },

    remote_debug: function (event) {
        confirmNotification(
            'This feature helps Zenput Support troubleshoot problems you may be having with your Zenput app. Please ' +
                'contact Zenput Support before pressing "Continue."',
            function (success) {
                if (!success) {
                    return;
                }

                enableWeinreRemoteDebug();
            },
            null,
            _t('mobile_login_continue_button'),
            _t('mobile_action_sheet_cancel')
        );

        return false;
    }
});

export const Debug = BaseToolbarNavPage.extend({
    template: '#tpl_debug',
    toolbarnav_title: function () {
        return 'Debug';
    },
    is_top_level_page: true,

    templateHelpers: function () {
        // flags for all platforms
        var flags = {
            disable_background_uploading: 'Disable Background Uploading',
            fail_file_uploads: 'Force File Upload Failures',
            disable_template_validation: 'Disable Submission validation',
            isReleaseBuild: isReleaseBuild(),
            getDebuggingFlag: getDebuggingFlag
        };

        // add web-specific debug flags
        if (isWeb()) {
            $.extend(flags, {
                css_native: 'Simulate native CSS',
                pending: 'Allow pending uploads'
            });
        }

        return {
            API_URL: API_URL,
            buildVersion: CordovaPlugins().BuildInfo?.version,
            flags: flags,
            isNative: isNative()
        };
    },

    initialize: function () {
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_debug');
    },

    events: {
        'click .flags_list': 'toggle_flag',
        'click #disable_debug': 'disable_debug'
    },

    toggle_flag: function (event) {
        var flag = $(event.target).data('flag');
        var is_enabled = getDebuggingFlag(flag);
        setDebuggingFlag(flag, !is_enabled);

        this.render();
        return false;
    },

    disable_debug: function () {
        disableDebugging();
        window.location.reload();
        return false;
    }
});

export const LanguageSelector = Backbone.Marionette.ItemView.extend({
    template: '#tpl_language_selector',
    active_locale: localization.DEFAULT_LOCALE,
    auto_change: true, // Allows users of this view to determine if the interface has to change language upon selection or not.

    triggers: {
        languange_change_noop: 'language_selector:change_language'
    },

    events: {
        'change .saveonchange': 'save_value'
    },

    templateHelpers: function () {
        return {
            active_locale: this.active_locale
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    save_value: function (event) {
        var this_ = this;
        var $target = $(event.target);
        var value = $target.val();
        this.active_locale = value;

        if (this.auto_change) {
            $f.mobile.settings.set('locale', value, function () {
                this_.on_select(value);
                this_._change_language(value);
            });
        }

        this_.render();
        return false;
    },

    _change_language: function (locale) {
        var this_ = this;
        $f.mobile.localization.set_locale(locale, true, function () {
            this_.trigger('language_selector:change_language');
        });
    },

    on_select: function () {
        console.error(
            'Programmer Error: You must provide an on_select function upon initialization of LanguageSelector.'
        );
    }
});

export const TemperatureUnitSelector = Backbone.Marionette.ItemView.extend({
    template: '#tpl_temperature_unit_selector',
    temperature_unit: null,

    triggers: {
        temperature_unit_change_noop: 'temperature_unit_selector:change_temperature_unit'
    },

    events: {
        'change .saveonchange': 'save_value'
    },

    templateHelpers: function () {
        return {
            temperature_unit: this.temperature_unit ?? $f.active_user.get('temperature_unit'),
            company_temperature_unit: $f.active_company.get('temperature_unit')
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    save_value: function (event) {
        var this_ = this;
        var $target = $(event.target);
        var value = $target.val();
        this.temperature_unit = value;

        if (this.auto_change) {
            $f.mobile.settings.set('temperature_unit', value, function () {
                this_.on_select(value);
                this_._change_temperature_unit(value);
            });
        }

        this_.render();
        return false;
    },

    _change_temperature_unit: function (temperature_unit) {
        var this_ = this;
        $f.active_user.set('temperature_unit', temperature_unit, function () {
            this_.trigger('temperature_unit_selector:change_temperature_unit');
        });
    },

    on_select: function () {
        console.error(
            'Programmer Error: You must provide an on_select function upon initialization of TemperatureUnitSelector.'
        );
    }
});
/**
 * This is a list of all the user settings on the device
 */
export const MobileUserProfileSettings = BaseToolbarNavPageLayout.extend({
    template: '#tpl_profile_settings',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_profile_settings');
    },
    model: null, // User

    regions: {
        r_language_selector: '.language_selector',
        r_temperature_unit_selector: '.temperature_unit_selector'
    },

    events: {
        'click .action_button': 'save_user',
        'change input': 'save_value'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        // we don't want to directly edit the global active_user, as we only want to persist changes
        // to it once the user presses save.
        this.model = new User($f.active_user.toJSON());

        Backbone.Validation.unbind(this);
        Backbone.Validation.bind(this);

        this.model.bind('validated:invalid', function (model, error) {
            $('.invalid').parents('.form-field').addClass('invalid');
        });
        this.model.bind('validated:valid', function (model, error) {
            this_.$el.find('.invalid').removeClass('invalid');
        });
    },

    save_value: function (event) {
        var target = $(event.target);
        var value = target.val();

        if (target.is(':checkbox')) {
            value = target.is(':checked') ? true : false;
        }
        this.model.set(target.attr('name'), value);

        return false;
    },

    save_user: function (event) {
        event.preventDefault();
        var this_ = this;

        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        if (!this.model.isValid(true)) {
            showErrorMessage(_t('mobile_message_profile_settings_error_fields'));
            return false;
        }

        showLoadingDialog();

        const oldLocale = this.model.get('userprofile__locale');
        const newLocale = this.r_language_selector.currentView.active_locale;

        // Even though we will call _change_language() below which will set the locale with
        // $f.mobile.localization.set_locale(), we also need to update our this.model User to send to
        // the server.
        this.model.set('userprofile__locale', newLocale);

        if ($f.flags.get_flag('set_temp_units_on_userprofile')) {
            const temperatureUnit =
                this.r_temperature_unit_selector.currentView.temperature_unit === 'null'
                    ? null
                    : this.r_temperature_unit_selector.currentView.temperature_unit;
            this.model.set('temperature_unit', temperatureUnit);
        }

        if ($f.flags.get_flag('set_temp_units_on_userprofile')) {
            this_.model.save(
                {},
                {
                    success: function (response) {
                        // Let's save our changes back to our global $f.active_user object
                        $f.active_user.set(this_.model.toJSON());

                        if (newLocale != oldLocale) {
                            // re-render the view to show the new language
                            this_._change_language();
                        }

                        hideLoadingDialog();
                        showSuccessMessage(_t('mobile_message_settings_success_saved'));
                    },
                    error: function (model, response) {
                        hideLoadingDialog();
                        if (!response || !response.results) {
                            showErrorMessage(response ? response.message : _t('mobile_message_settings_error_saved'));
                            return;
                        }

                        // just grab the first error and display it
                        response.results.every(function (error) {
                            showErrorMessage(error.message);

                            Backbone.Validation.callbacks.invalid(self, error.name, error.message, 'name');
                            this_.model.trigger('validated:invalid');
                            return false;
                        });
                    }
                }
            );
        } else {
            this_._change_language(function () {
                this_.model.save(
                    {},
                    {
                        success: function (response) {
                            // Let's save our changes back to our global $f.active_user object
                            $f.active_user.set(this_.model.toJSON());

                            hideLoadingDialog();
                            showSuccessMessage(_t('mobile_message_settings_success_saved'));
                        },
                        error: function (model, response) {
                            hideLoadingDialog();
                            if (!response || !response.results) {
                                showErrorMessage(
                                    response ? response.message : _t('mobile_message_settings_error_saved')
                                );
                                return;
                            }

                            // just grab the first error and display it
                            response.results.every(function (error) {
                                showErrorMessage(error.message);

                                Backbone.Validation.callbacks.invalid(self, error.name, error.message, 'name');
                                this_.model.trigger('validated:invalid');
                                return false;
                            });
                        }
                    }
                );
            });
        }
        return false;
    },

    _change_language: function (callback) {
        callback = callback || function () {};
        var this_ = this;

        $f.mobile.localization.set_locale(this.model.get_locale(), true, function () {
            this_.render();
            $f.application.content.currentView.r_leftmenu.currentView.render();
            callback();
        });
    },

    onRender: function () {
        var this_ = this;
        this.r_language_selector.show(
            new LanguageSelector({
                active_locale: this_.model.get_locale(),
                on_select: function (selected_locale) {
                    this_.model.set('userprofile__locale', selected_locale);
                },
                auto_change: false
            })
        );

        if ($f.flags.get_flag('set_temp_units_on_userprofile')) {
            this.r_temperature_unit_selector.show(
                new TemperatureUnitSelector({
                    temperature_unit: this.model.get('temperature_unit'),
                    on_select: function (selected_unit) {
                        this_.model.set('temperature_unit', selected_unit);
                    },
                    auto_change: false
                })
            );
        }
    },

    onBeforeClose: function () {
        Backbone.Validation.unbind(this);
        this.model.unbind('validated:valid');
    }
});

/**
 * This is a list of all the user notification settings on the device
 */
export const NotificationSettings = BaseToolbarNavPageLayout.extend({
    template: '#tpl_notification_settings',
    collection: UserNotificationSettings,
    allow_reverse_transitions: true,
    regions: {
        r_notification_communication: '.r_notification_communication',
        r_notification_settings_forms_and_triggers: '.r_notification_settings_forms_and_triggers',
        r_notification_settings_tasks: '.r_notification_settings_tasks'
    },

    // @author:david this shouldn't show up if the collection hasn't been fetched yet @jira:MOBILE-576
    getEmptyView: function () {
        return EmptyListMessage.extend({
            i18n_key_message: 'mobile_message_show_error_message',
            icon_class: 'df-alert'
        });
    },

    className: 'form_container',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_notification');
    },
    events: {
        'click .action_button': 'save_settings',
        'change input': 'save_value'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        showLoadingDialog();
        this.collection = new UserNotificationSettings();
        this.collection.fetch({
            timeout: 10000,
            success: () => {
                hideLoadingDialog();
                this._show_notifications_settings();
            },
            error: () => {
                hideLoadingDialog();
            }
        });
    },

    _get_collection_by_category: function (category) {
        const categoryModels = this.collection.where({ category: category });
        return new UserNotificationSettings(categoryModels);
    },

    _show_notifications_settings: function () {
        this.r_notification_communication.show(
            new NotificationSettingsSet({
                collection: this._get_collection_by_category('communication')
            })
        );

        this.r_notification_settings_forms_and_triggers.show(
            new NotificationSettingsSet({
                collection: this._get_collection_by_category('forms_and_triggers')
            })
        );

        this.r_notification_settings_tasks.show(
            new NotificationSettingsSet({
                collection: this._get_collection_by_category('tasks')
            })
        );
    },

    save_settings: function (event) {
        event.preventDefault();
        if (!isConnected()) {
            showOfflineMessage();
        }

        showLoadingDialog();

        this.collection.save({
            success: function () {
                hideLoadingDialog();
                $f.active_user.fetch({
                    force: true,
                    success: () => {
                        if (!$f.active_user.get('local_notifications_enabled')) {
                            return $f.mobile.notifications.cancelAllFutureLocalNotifications();
                        }

                        const my_tasks = new MyTasks([], {
                            paginator: new Paginator({ filter: 'me', status: 'open' })
                        });
                        my_tasks.fetch({ remove: false });
                    }
                });
                showSuccessMessage(_t('mobile_message_settings_success_saved'));
            },
            error: function () {
                hideLoadingDialog();
                if (!isConnected()) {
                    showOfflineMessage();
                } else {
                    showErrorMessage(_t('mobile_message_settings_error_saved'));
                }
            }
        });
        return false;
    }
});

export const NotificationSettingsSet = Backbone.Marionette.CollectionView.extend({
    collection: UserNotificationSettings,
    itemView: NotificationSettingsItem,
    allow_reverse_transitions: true,
    tagName: 'ul',
    className: 'form_container card'
});

/**
 *  The list of form templates
 */
export const FormTemplateItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_form_template_item',
    tagName: 'li',

    events: {
        'click .navlink': 'click_navlink'
    },

    click_navlink: function (event) {
        $f.mobile.load_collection(this.model.get('id'), null, true);
        return false;
    }
});

export const FormTemplatesPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_form_templates_list',
    className: 'form_template_list',
    allow_reverse_transitions: true,
    is_top_level_page: true,
    active_search: null, // Backbone.Model

    toolbarnav_title: function () {
        return _t('mobile_left_menu_my_forms');
    },

    regions: {
        r_search_bar: '.search_bar',
        r_form_templates_list: '.page_scrollable'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_form_templates');

        this.active_search = this.active_search || new Backbone.Model({ value: '' });
        $f.mobile.nav.add_options({ active_search: this.active_search });
    },

    onRender: function () {
        this.r_search_bar.show(
            new MobileSearchBar({
                collection: this.collection,
                default_value: this.active_search.get('value'),
                search_delay: 100,
                search: (query, callback) => {
                    this.active_search.set('value', query);
                }
            })
        );

        this.r_form_templates_list.show(new FormTemplatesPageList({ active_search: this.active_search }));
    }
});

/**
 * This is a list of all the form templates on the device
 */
export const FormTemplatesPageList = Backbone.Marionette.CollectionView.extend({
    className: 'pagelist',
    tagName: 'ul',
    itemView: FormTemplateItem,
    active_search: null, // Backbone.Model

    initialize: function (options) {
        this.collection = $f.mobile.form_templates;

        options = options || {};
        $.extend(this, options);

        this._show_loader();
        $f.mobile.form_templates.get_user_templates(this._on_template_load_success.bind(this));

        this.listenTo(this.active_search, 'change:value', this.render);
    },

    // emptyView didnt work, had to use getEmptyView
    getEmptyView: function () {
        if (this._is_loading) {
            return null;
        }
        return EmptyListMessage;
    },

    getItemView: function (item) {
        const title = item.get('title');
        const search = this.active_search.get('value');

        if (!title.toLowerCase().includes(search.toLowerCase())) {
            return EmptyView;
        }

        return FormTemplatesPageList.__super__.getItemView.apply(this, arguments);
    },

    on_resume: function () {
        this._show_loader();
        $f.mobile.form_templates.get_user_templates(this._on_template_load_success.bind(this));
    },

    _show_loader: function () {
        if ($f.mobile.form_templates.isEmpty()) {
            showLoadingDialog();
        }
    },

    _on_template_load_success: function () {
        this._is_loading = false;
        hideLoadingDialog();
        this.render();
    }
});

/**
 * List of Form Drafts
 */
export const FormDraftItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_form_draft_item',
    tagName: 'li',
    form_template: null,

    events: {
        'click .delete_draft': 'delete_draft',
        'click a': 'load_draft'
    },

    templateHelpers: function () {
        var date = moment(this.model.get('date_modified'));
        return {
            date: date.format('ddd, MMM D'),
            time: date.format('h:mm a'),
            account_name: this.model.get('account_name')
        };
    },

    load_draft: function (event) {
        var this_ = this;
        showLoadingDialog(); // show_collection below will hide our loading dialog for us
        $f.mobile.form_drafts.get_record(this.model.id, null, function (result) {
            if (!result) {
                hideLoadingDialog();
                showErrorMessage();
                $f.mobile.logging.log_error('Unable to retrieve draft from disk', {
                    draft_id: this_.model.id
                });
                return;
            }

            $f.mobile.show_collection(result, { animate: true });
        });
        return false;
    },

    delete_draft: function (event) {
        var view = this;
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }
        confirmNotification(
            _t('mobile_message_drafts_form_confirm'),
            function (success) {
                if (!success) {
                    return;
                }
                $f.application.vent.trigger('mobile:delete_draft', view.model.get('id'));
            },
            _t('mobile_message_drafts_confirm_delete'),
            _t('mobile_common_button_yes'),
            _t('mobile_common_button_no')
        );

        return false;
    }
});

export const FormDraftsPage = BaseToolbarNavPage.extend({
    template: '#tpl_form_drafts_list',
    itemView: FormDraftItem,
    itemViewContainer: 'ul.pagelist',
    className: 'form_template_list drafts_list',
    collection: null,
    toolbarnav_title: function () {
        return _t('mobile_left_menu_drafts');
    },
    is_editing: false,
    allow_reverse_transitions: true,
    is_top_level_page: true,
    toolbarnav_show_add_task: false,

    events: {
        'click .edit_drafts': 'edit_drafts'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var _this = this;

        // force re-fetch of synced drafts
        $f.mobile.form_drafts.get_user_drafts();

        // do not use the collection in options, because history could give us an out-of-date collection
        this.collection = $f.mobile.form_drafts;
        this.collection.sort();

        this.listenTo($f.application.vent, 'mobile:delete_draft', function (id) {
            _this.delete_draft(id);
        });
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_drafts');
    },

    getEmptyView: function () {
        return EmptyListMessage.extend({
            i18n_key_message: 'mobile_drafts_message',
            icon_class: 'df-drafts'
        });
    },

    on_toolbarnav_init: function () {
        if (!$f.mobile.form_drafts.draft_count || $f.mobile.form_drafts.draft_count === 0) {
            return;
        }

        this.toolbarnav.add_actions([
            {
                container_class: 'edit_drafts',
                icon_class: 'df-edit',
                i18n_key_text: 'mobile_drafts_form_text',
                display: 'icon'
            }
        ]);
    },

    edit_drafts: function (event) {
        var view = this;
        if (this.is_editing) {
            // Hide Edit Button
            this.is_editing = false;
            view.$el.find('.pagelist').removeClass('editing');
            this.$el.find('.edit_drafts i').velocity({ opacity: 0 }, 'fast', function () {
                view.$el.find('.edit_drafts i').removeClass('df-success').addClass('df-edit');
                view.$el.find('.datetime, .edit_drafts i').velocity({ opacity: 1 }, 'fast');
            });
        } else {
            // Show Edit Button
            this.is_editing = true;
            view.$el.find('.pagelist').addClass('editing');
            this.$el.find('.datetime, .edit_drafts i').velocity({ opacity: 0 }, 'fast', function () {
                view.$el.find('.edit_drafts i').removeClass('df-edit').addClass('df-success');
                view.$el.find('.edit_drafts i').velocity({ opacity: 1 }, 'fast');
            });
        }
    },

    delete_draft: function (id) {
        if ($f.mobile.form_drafts.get(id).get('is_synced') === true) {
            $f.mobile.form_drafts.get_record(id, null, function (draft) {
                draft.delete();
            });
        }
        this.collection.remove_records(id);
        if (this.collection.length == 0) {
            this.render();
        }
    }
});

/**
 * A Marionette Region that shows and hides its view with a sliding transition.
 *
 * Views that do not want to be animated can set use_transitions to false. Views that would not like to
 * animate forwards but would like to animate backwards should set `allow_reverse_transitions` to `true`.
 *
 * Animations work as following:
 *
 * - Before closing view: puts old HTML into a fake content div (.fake_main_content)
 * - After showing: move the real content area (.main_content) to the side so that the user still sees the fake content
 * - Animate the real content area into place and hide the fake content area
 *
 * Another example of using Regions for animations: https://gist.github.com/brian-mann/3947145
 */
export const TransitionRegion = Backbone.Marionette.Region.extend({
    currently_replacing_view: false, // used to communicate between show() and close()
    ANIMATION_TIME_MS: 250,

    _old_view: null, // if our animation is replacing an old view, we need to hold on to our `this`. @see _close_old_view()

    /**
     * [show description]
     * @param  {Backbone.Marionette.View}   view        The View to Show
     * @param  {dict}                       options     Our own transition-related options for this view
     *      is_historic_view: if true, we'll try to reverse the view's normal animation direction
     *      disable_transition: if true, override other options and do not animate the view into place
     */
    show: function (view, options) {
        options = options || {};
        var this_ = this;

        var _reverse_transition = options.is_historic_view && view.allow_reverse_transitions;
        var _disable_transition = options.disable_transition;

        view.is_historic_view = options.is_historic_view; // view needs to know this so it can restore scrollbar position only for historic views

        // Just do a normal show() if:
        if (
            _disable_transition || // transitions are explicitly turned off
            (!view.use_transitions && !_reverse_transition) || // the view is new and isn't using transitions
            ($f.mobile.nav._views.length == 1 && !view.is_historic_view) // the view is new (we're moving forward) and the first in history (prevents transition when redirecting from link)
        ) {
            return TransitionRegion.__super__.show.call(this, view);
        }

        // Before we show anything, create a new main_content and set that to our $el:
        this.$el.after(
            `<${$('.main_content').prop('tagName').toLowerCase()} class="${
                $('.main_content').attr('class') || ''
            }" style="${$('.main_content').attr('style') || ''}"></${$('.main_content').prop('tagName').toLowerCase()}>`
        );
        this.$el.addClass('stale');
        this.$el = this.$el.siblings('.main_content:not(.stale)');
        this.el = this.$el.get(0);

        // Set some common vars:
        var $main_content = this.$el;
        var $stale_content = this.$el.siblings('.main_content.stale');
        var $sliding_content = _reverse_transition ? $stale_content : $main_content; // Does the fake or real content slide?
        var transition_from = _reverse_transition ? '0%' : '100%';
        var transition_to = _reverse_transition ? '100%' : '0%';

        // hide the new main content until we start the animation, as it would otherwise appear on top of the old main content, showing a flicker
        $main_content.css('opacity', 0);

        // Prepare for animation
        this_.currently_replacing_view = true; // tell close() to defer the close() until animation finishes
        TransitionRegion.__super__.show.call(this_, view); // will call this.close()

        // Set the stage
        $.Velocity.hook($sliding_content, 'z-index', 99999); // $.Velocity.hook() is like $().css()
        $.Velocity.hook($sliding_content, 'translateX', transition_from);

        // Block the user from touching anything on the page until the transition finishes
        $('.page_transition_touchblock').show();

        // Aaaand action!
        $sliding_content.velocity(
            {
                translateX: transition_to
            },
            {
                duration: this_.ANIMATION_TIME_MS,
                easing: _reverse_transition ? 'easeInCirc' : 'easeOutCirc', // these easings will start/stop slow --> fast for 'reverse' animation, fast --> slow for normal
                begin: function () {
                    $main_content.css('opacity', 1);
                },
                complete: function () {
                    $main_content.css('z-index', '');
                    this_._close_old_view();
                    $stale_content.remove(); // throw out old view
                    $('.page_transition_touchblock').hide(); // now that we're done, allow interaction with the page
                }
            }
        );
    },

    close: function () {
        /**
         * This is pretty hacky, but it works. Essentially, we need to defer the closing of our currentView
         * until the animation is done playing, UNLESS we are not playing an animation.
         *
         * If we are playing an animation, we will immediately clone `this` so that we can still access this.currentView.
         * Once we are ready to close the old view, we will call our super.close(), but pass it our `old_this` so that it calls
         * our old `currentView`'s `close()` instead of our actual `currentView`'s close().
         */

        if (!this.currently_replacing_view) {
            return TransitionRegion.__super__.close.apply(this, arguments);
        }

        this._old_this = jQuery.extend(true, {}, this); // old_this will hold on to our old currentView and will be closed in _close_old_view()
    },

    /**
     * If there is an old `this` we've been waiting to close, close it.
     * @see close()
     * @see  show()
     */
    _close_old_view: function () {
        if (!this._old_this) {
            return;
        }

        TransitionRegion.__super__.close.apply(this._old_this, arguments);
        this.currently_replacing_view = false;
        this._old_this = null;
    }
});

/**
 * The Layout for the mobile app: the form, and the two drawers.
 */
export const MobileAppLayout = Backbone.Marionette.Layout.extend({
    template: '#tpl_mobile_app_layout',

    // These will be replaced by data from options.initial_page_data if available:
    initial_page_options: { is_top_level_page: true }, // default options to pass to initial view for r_main_content
    _initial_page_class: null, // do not set by hand! Will be set in initialize() based on options.initial_page_data
    // and used by get_initial_page_class()
    /**
     * Get the initial view to show in r_main_content.
     */
    get_initial_page_class: function (callback) {
        if ($f.active_user.is_submitter()) {
            return this._initial_page_class || TasksPage;
        }

        return this._initial_page_class || $f.mobile.default_start_page_class;
    },

    regions: {
        r_leftmenu: '#leftmenu',
        r_main_content: {
            selector: '.main_content',
            regionType: TransitionRegion
        }
    },

    initialize: function (options) {
        options = options || {};

        $f.mobile.nav.clear(); // in case we're showing the app layout a second time

        // If sent, store initial_page_data as this.initial_page_class and this.initial_page_options
        // to be used in onShow()
        if (options.initial_page_data && options.initial_page_data.klass) {
            this._initial_page_class = options.initial_page_data.klass;
            delete options.initial_page_data.klass;
            this.initial_page_options = options.initial_page_data;
        }
    },

    onShow: function () {
        setStatusBarColor();

        $('body').removeClass('snapjs-left'); // make sure this isn't here (sometimes can be left over from pervious MobileAppLayout instances)

        this.r_leftmenu.show(new LeftMenu());

        $f.mobile.nav.show(this.get_initial_page_class(), this.initial_page_options);
    }
});

export const LeftMenu = Backbone.Marionette.ItemView.extend({
    template: '#tpl_leftmenu',
    show_nav_locations: true,

    // Since SnapJS has no direct callback for closing the drawer, we'll just set and unset this.
    // Yes, this is irritating and hacky.
    _temporary_drawer_close_callback: null,

    snapper: null, // SnapJS instance

    events: {
        'click .upload_background': 'click_upload_background',
        'click .logout': 'logout',
        'click .show_oia_modal': 'show_oia_modal',
        'click .nav_notifications': 'show_notifications',
        'click .nav_form_templates': 'show_form_templates',
        'click .nav_announcements': 'show_announcements',
        'click .nav_dashboard': 'show_dashboard',
        'click .nav_settings': 'show_settings',
        'click .nav_drafts': 'show_drafts',
        'click .nav_history': 'show_history',
        'click .nav_locations': 'show_accounts',
        'click .nav_tasks': 'show_tasks',
        'click .nav_projects': 'show_projects',
        'click .nav_debug': 'show_debug',
        'click .zenput_docs': 'show_zenput_docs',
        'click .zenput_support': 'show_zenput_support'
    },

    templateHelpers: function () {
        return {
            draft_count: $f.mobile.form_drafts.draft_count,
            unread_notifications_count: $f.mobile.unread_notifications_count.get('count'),
            show_nav_locations: this.show_nav_locations,
            isIos: isIos(),
            isWeb: isWeb(),
            isNative: isNative(),
            isWindows: isWindows(),
            isAndroid: isAndroid(),
            isDebuggingEnabled: isDebuggingEnabled(),

            // @jira:ZP-13355 Zendesk doesn't work with the version of Edge in our Cordova Windows app
            disableChat: $f.active_company.has_feature('disable_intercom') || (isWindows() && isNative())
        };
    },

    initialize: function (options) {
        var this_ = this;

        options = options || {};
        $.extend(this, options);

        this.listenTo($f.application.vent, 'mobile:close_leftmenu', function () {
            this_.close_drawer();
        });
        this.listenTo($f.application.vent, 'mobile:toggle_leftmenu', function () {
            this_.toggle();
        });
        this.listenTo($f.application.vent, 'mobile:set_num_uploads', this_.show_num_uploading);

        this.listenTo($f.mobile.form_drafts, 'add remove', this_.render);
        $f.mobile.uploads.for_each_pending_upload_structure(function (pending_upload_structure) {
            this_.listenTo(pending_upload_structure, 'add remove', this_.render);
        });

        $f.mobile.unread_notifications_count.on('change', this_.render);
        $f.mobile.form_drafts.get_user_draft_count(this_.render);
    },

    onBeforeRender: function () {
        if (this.$el.find('.selected').length > 0) {
            this._selected_navlink_classname = this.$el.find('.selected').prop('class');
            this._selected_navlink_classname = this._selected_navlink_classname.replace('selected', '');
        }
    },

    onRender: function () {
        var this_ = this;

        if (this._selected_navlink_classname) {
            $(this.$el.get(0).getElementsByClassName(this._selected_navlink_classname)).addClass('selected');
            this._selected_navlink_classname = null;
        }

        /**
         * Set the specified LeftMenu item as "selected"
         * @param  {string} class_name The class name for the nav link to highlight
         */
        this.listenTo($f.application.vent, 'mobile:set_active_leftmenu_item', function (class_name) {
            this_.$el.find('.selected').removeClass('selected');
            this_.$el.find('.' + class_name).addClass('selected');
        });
    },

    onShow: function () {
        if (this.snapper) {
            this.snapper.enable(); // rebind snapper's events
            return;
        }

        this._setup_snapper();
    },

    // Initialize SnapJS!
    _setup_snapper: function () {
        var this_ = this;

        this.snapper = new Snap({
            element: $('#pagebody').get(0),
            dragger: $('#leftmenu_dragger').get(0),
            disable: 'right', // we don't have a right menu :)
            touchToDrag: isAndroid() || isIos() || isWindows(),
            tapToClose: false, // we'll handle this ourselves
            slideIntent: 80
        });

        this.snapper.on('animated', function (event) {
            if (this_.is_open()) {
                this_.on_drawer_open();
            } else {
                this_.on_drawer_closed();
            }
        });

        $('#pagebody .leftmenu_open_pagebody_blocker').off('click.ie_close_drawer');
        $('#pagebody .leftmenu_open_pagebody_blocker').on('click.ie_close_drawer', function () {
            this_.close_drawer();
        });
    },

    on_drawer_open: function () {},

    on_drawer_closed: function () {
        if (this._temporary_drawer_close_callback) {
            this._temporary_drawer_close_callback();
        }
        this._temporary_drawer_close_callback = null;
    },

    show_num_uploading: function () {
        var total_num_pending = $f.mobile.uploads.get_num_pending_submission_uploads(true);
        if (total_num_pending > 0) {
            $('.upload_background').addClass('error').show().find('.count').html(total_num_pending);
        } else {
            $('.upload_background').removeClass('error').hide();
        }
    },

    is_open: function () {
        try {
            return this.snapper.state().state == 'left';
        } catch (e) {
            return false;
        }
    },

    open_drawer: function () {
        $f.mobile.unread_notifications_count.fetch();
        this.snapper.open('left');
    },

    close_drawer: function () {
        this.snapper.close('left');
    },

    toggle: function () {
        if (this.is_open()) {
            this.close_drawer();
        } else {
            this.open_drawer();
        }
    },

    click_upload_background: function (event) {
        this.close_drawer();

        this._temporary_drawer_close_callback = function () {
            if (!isConnected()) {
                showOfflineMessage();
                $f.application.vent.trigger('mobile:set_num_uploads');
                return;
            }

            $f.mobile.uploads.start_upload();
        };
        return false;
    },

    logout: function (event) {
        var msg = _t('mobile_common_logout_message');
        var unsynced_drafts = $f.mobile.form_drafts.where({ is_synced: false }).length;
        if (unsynced_drafts > 0) {
            msg += '\n\n' + _t('mobile_message_log_out_confirm_unsynced_drafts', { count: unsynced_drafts });
        }

        confirmNotification(
            msg,
            function (success) {
                if (success) {
                    $f.mobile.logout();
                }
            },
            _t('mobile_common_logout_message')
        );

        return false;
    },

    show_oia_modal: function () {
        $f.mobile.nav.clear();
        $f.mobile.nav.show_modal(OiaDialog, {});
        return false;
    },

    _show_page: function (view_class, view_options) {
        view_options = view_options || {};
        $f.mobile.nav.clear();

        this.close_drawer();
        $f.mobile.nav.show(view_class, view_options);
        return false;
    },

    show_notifications: function () {
        import('./comments/views').then((module) => {
            this._show_page(module.NotificationsPage, {});
        });
    },

    show_dashboard: function () {
        import('./dashboard/views').then((module) => {
            this._show_page(module.MobileDashboard, {
                is_top_level_page: true
            });
        });
        return false;
    },

    show_form_templates: function () {
        this._show_page(FormTemplatesPage);
        return false;
    },

    show_announcements: function () {
        import('./announcements/views').then((module) => {
            this._show_page(module.AnnouncementsPage);
        });
        return false;
    },

    show_settings: function () {
        this._show_page(Settings);
        return false;
    },

    show_debug: function () {
        this._show_page(Debug);
        return false;
    },

    show_drafts: function () {
        this._show_page(FormDraftsPage);
        return false;
    },

    show_history: function (event) {
        import('../backbone/features/mobileForm/views').then((module) => {
            this._show_page(module.AllSubmissionsHistoryList);
        });
        return false;
    },

    show_accounts: function (event) {
        this._show_page(AccountsView);
        return false;
    },

    show_tasks: function (event) {
        this._show_page(TasksPage);
        return false;
    },

    show_projects: function (event) {
        this._show_page(ProjectsView);
        return false;
    },

    show_zenput_docs: function (event) {
        $f.mobile.open_uri('https://support.zenput.com/');
        return false;
    },

    show_zenput_support: function (event) {
        this.close_drawer();
        const currentWidget = getLocalStorageWithExpiry(SUPPORT_CHAT_WIDGET_KEY);

        if (currentWidget !== SUPPORT_CHAT_ZENDESK_VALUE) {
            if (isForethoughtAvailable()) {
                showForethoughtWidget();
            }
        } else {
            showZendeskWidget();
        }

        return false;
    },

    onBeforeClose: function () {
        $f.application.vent.off('mobile:close_leftmenu');
        $f.application.vent.off('mobile:toggle_leftmenu');
        $f.application.vent.off('mobile:set_num_uploads');
        $('#pagebody .leftmenu_open_pagebody_blocker').off('click.ie_close_drawer');
    },

    /**
     * There is logic within the mobile_dashboard for submitters where they should not see the locations link
     */
    hide_account_link: function () {
        this.show_nav_locations = false;
        this.$el.find('.nav_locations').hide();
    }
});

/**
 * Modal Dialog to prompt user to download our native app, open in
 * native app, or just use webapp
 */
export const OiaDialog = Backbone.Marionette.Layout.extend({
    template: '#tpl_oia_dialog',

    on_dismiss_for_web: function () {
        this.close();
    }, // callback for when the user dismisses the OIA dialog and wants to proceed on the web

    events: {
        'click #oia_btn': 'open_app',
        'click #download_app_btn': 'download_app',
        'click #oiw_btn': 'open_web'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        return {
            isIos: isIos(),
            isWindows: isWindows()
        };
    },

    onShow: function () {
        $f.application.vent.trigger('mobile:close_leftmenu');
    },

    /**
     * Convert our #hash into a zenput:// URI that the device understands.
     */
    open_app: function (event) {
        location.href = 'zenput://' + window.location.hash.replace('#', '');
        return false;
    },

    download_app: function (event) {
        downloadApp();
        return false;
    },

    open_web: function (event) {
        var this_ = this;
        $f.mobile.settings.set('oia', new Date().getTime(), function () {
            this_.on_dismiss_for_web();
        });
        return false;
    }
});

/**
 * A single row of the tasks list page.
 * @see MobileTasksList
 */
export const TaskItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_task_item',
    model: null, // MobileTask
    tagName: 'li',
    toolbarnav_title: null, // Needs to be passed down to handle showing project vs task title
    always_display_time: false, // If true when displaying the task always show the date and time.

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        var is_followup = !!(this.model.get('source').get('rule') && this.model.get('source').get('rule').id);
        var is_filtering_by_complete = this.model.collection.paginator.get('status') === 'closed';
        var show_strikeout =
            _.contains(['complete', 'complete_with_exception'], this.model.get('current_state')) &&
            !is_filtering_by_complete;
        var current_state = this.model.get_current_state_string();
        var is_due_soon = this.model.is_task_due_in_one_hour();

        let date_start = this.model.get_date_start();
        let date_start_day = this.get_day_str(date_start);
        let date_start_time = this.get_time_str(date_start);

        let date_due = this.model.get_date_due();
        let date_due_day = this.get_day_str(date_due);
        let date_due_time = this.get_time_str(date_due);

        const show_completed_late =
            _.contains(['complete', 'complete_with_exception'], this.model.get('current_state')) &&
            this.model.get('is_completed_late');

        return {
            current_state: current_state,
            date_due_by: date_due
                ? _t('mobile_tasks_bucket_due_by_at', { date: date_due_day, time: date_due_time })
                : null,
            date_start: _t('mobile_tasks_bucket_open_at', { date: date_start_day, time: date_start_time }),
            is_complete_with_exceptions: this.model.get('current_state') === 'complete_with_exception',
            show_completed_late,
            is_followup: is_followup,
            is_due_soon: is_due_soon,
            is_overdue: this.model.get('current_state') === 'overdue',
            location_name: this.model.get('account') ? this.model.get('account').get('name') : null,
            should_show_location: !$f.mobile.tasks.utils.is_single_account_submitter(),
            show_strikeout: show_strikeout,
            state_icon: $f.mobile.get_state_icon_for_task(
                this.model.get('current_state'),
                this.model.get('is_completed_late')
            ),
            assignee_name: this.model.get('assignee') ? this.model.get('assignee').get('display_name') : null
        };
    },

    events: {
        'click .navlink': 'show_task'
    },

    show_task: function (event) {
        $f.mobile.nav.show(TaskDetailsPage, {
            model: this.model,
            toolbarnav_title: this.toolbarnav_title
        });
        return false;
    },

    get_time_zone_code: function () {
        var date_due = this.model.get_date_due();
        return date_due ? date_due.format('z') : '';
    },

    get_day_str: function (datetime) {
        let moment_date = moment(datetime);
        if (moment_date.isSame(moment(), 'day')) {
            return _t('mobile_tasks_toolbarnav_header_today');
        }
        return moment_date.format('MMM DD');
    },

    get_time_str: function (datetime) {
        return moment(datetime).format('h:mm a z');
    }
});

// @todo @author:david we should eventually use this as a base class or mixin for all list views that require infinite scroll:
export const BaseToolbarNavList = Backbone.Marionette.CollectionView.extend({
    tagName: 'ul',
    className: null, // ex 'pagelist tasks_list' class of the ul element
    should_show_section_legend_buttons: false,

    _last_fetch_timestamp: 0,

    constructor: function () {
        BaseToolbarNavList.__super__.constructor.apply(this, arguments);
        this.$el.addClass('pagelist');
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onShow: function () {
        this._setup_infinite_scroll();
    },

    /**
     * Adds a loading spinner to the bottom of the list (with invisible styling). This will remove
     * any existing .infinite_scroll_loading spinner before appending a new one.
     */
    _show_loading_spinner: function () {
        this.$el.parent().siblings('.infinite_scroll_loading').css('visibility', 'visible');
    },

    _remove_loading_spinner: function () {
        this.$el.parent().siblings('.infinite_scroll_loading').css('visibility', 'hidden');
    },

    // @note: @author:david: when more lists inherit from this class, they can
    // implement this method in a way that works specifically for each class
    perform_scroll_fetch: function () {
        var this_ = this;
        this._show_loading_spinner();
        import('./local_records').then((module) => {
            this.collection.fetch({
                force: this.collection instanceof module.LocalRecords, // for local_records: fetch from server
                success: function () {
                    this_._remove_loading_spinner();
                },
                error: function () {
                    this_._remove_loading_spinner();
                },
                remove: false
            });
        });
    },

    /**
     * after rendering, setup infinite scroll.  note the timeout
     * delay.  without it, the scrollTop wouldnt happen fast enough and start
     * firing requests for the next page
     */
    _setup_infinite_scroll: function () {
        var this_ = this;

        var $pg_scrollable = this.$el.parents('.page_scrollable');
        setTimeout(function () {
            $pg_scrollable.scrollTop(0); // when the page inits, be sure to jump to the top.  inifite scroll will keep going otherwise
            $pg_scrollable.off('scroll');
            $pg_scrollable.on('scroll', function () {
                if ($pg_scrollable.scrollTop() < $pg_scrollable.get(0).scrollHeight - $pg_scrollable.height() - 100) {
                    return;
                }

                // limit fetches to once every 2 seconds
                if (this_._last_fetch_timestamp + 1000 > new Date().getTime()) {
                    return;
                }
                this_._last_fetch_timestamp = new Date().getTime();

                if (!isConnected()) {
                    return;
                }

                var col_paginator = this_.collection.paginator;
                if (!this_.collection.paginator) {
                    $f.mobile.logging.log_error(
                        'Attempting to use infinite scroll with collection that does not have a paginator'
                    );
                    return;
                }

                col_paginator.set('start', col_paginator.get('start') + col_paginator.get('limit'));
                this_.perform_scroll_fetch();
            });
        }, 100);
    }
});

export const MobileTasksList = Backbone.Marionette.CollectionView.extend({
    tagName: 'ul',
    className: 'tasks_list pagelist',

    collection: null, // MobileTasks
    itemView: TaskItem,
    toolbarnav_title: null,
    zenput_code: null,
    allow_retry: false,
    empty_view_i18n_key: null,
    always_display_time: false, // If true when displaying the task always show the time.

    attributes: {
        'data-cy': 'tasks-list'
    },

    itemViewOptions: function () {
        return {
            toolbarnav_title: this.toolbarnav_title,
            always_display_time: this.always_display_time
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        MobileTasksList.__super__.initialize.apply(this, arguments);
        this.collection = this.collection || new MobileTasks();
        this.empty_view_i18n_key = this.empty_view_i18n_key || 'mobile_empty_list_message_no_tasks';
    },

    onRender: function () {
        var this_ = this;
        if (this.children.first() instanceof EmptyListMessage) {
            this.listenTo(this.children.first(), 'empty_view:retry_fetch', function () {
                this_.trigger('empty_view:retry_fetch');
            });
        }
    },

    getEmptyView: function () {
        return EmptyListMessage.extend({
            icon_class: 'df-success',
            i18n_key_message: this.empty_view_i18n_key,
            zenput_codes: this.zenput_code,
            allow_retry: this.allow_retry
        });
    }
});

export const BaseTaskProjectFilter = BaseToolbarNavPageLayout.extend({
    template: null,
    collection: null, // TaskSearchOptions
    toolbarnav_title: null, // we'll set this once we know it
    use_transitions: true,
    className: null,
    toolbarnav_show_add_task: false,
    toolbarnav_center_title: true,

    _last_known_scrollpos: 0,

    events: {
        'click .pagelist a:not(.team_filter, .account_filter)': 'update_search_option',
        'click .pagelist .team_filter': 'show_team_selector_modal',
        'click .pagelist .clear_team_filter': 'clear_team_filter',
        'click .apply_filters': 'go_back'
    },

    initialize: function (options) {
        // project page does not currently use allowed filters
        this.allowed_filters = this.allowed_filters || {};

        options = options || {};
        $.extend(this, options);
    },

    onBeforeRender: function () {
        if (!this._isShown) {
            return;
        }
        this._last_known_scrollpos = this.$el.find('.page_scrollable').scrollTop();
    },

    onRender: function () {
        var this_ = this;
        var ownership_filter = this.collection.findWhere({ field: 'filter' });

        if (ownership_filter.get('value') == 'me') {
            this.hide_team_filter();
        } else {
            this.show_team_filter();
        }

        if (!this_._isShown) {
            return;
        } // if not shown yet, leave any scrolling to $f.mobile.nav.restore_scroll_position()

        setTimeout(function () {
            this_.$el.find('.page_scrollable').scrollTop(this_._last_known_scrollpos);
        }, 1);
    },

    hide_team_filter: function () {
        this.$el.find('.team_filter, .toggle_buttons .clear_team_filter').addClass('disabled');
    },

    show_team_filter: function () {
        this.$el.find('.team_filter, .toggle_buttons .clear_team_filter').removeClass('disabled');
    },

    clear_team_filter: function () {
        this.collection.findWhere({ field: 'group_id' }).set({
            display_value: '',
            value: null
        });
        this.render();
        return false;
    },

    go_back: function () {
        var this_ = this;
        var args_ = arguments;
        this.collection.save_local(function () {
            this_.collection.trigger('filters_updated');
            $f.mobile.tasks.reset_buckets_collapsed_states();
            if (this_.is_modal) {
                this_.close();
            } else {
                BaseTaskProjectFilter.__super__.go_back.apply(this_, args_);
            }
        });
        return false;
    },

    update_search_option: function (event) {
        var target = $(event.target);
        if (!target.is('a')) {
            target = target.parents('a');
        }

        if (target.hasClass('disabled')) {
            return false;
        }
        // address clicking an icon inside the actual target we want
        if (!target.hasClass('filter_button') && target.parents('.filter_button').length > 0) {
            target = target.parents('.filter_button');
        }

        var field = target.data('field');
        var value = target.attr('value');
        if (value && field === 'is_active') {
            value = JSON.parse(target.attr('value'));
        }

        this.collection.findWhere({ field: field }).set({ value: value });

        var ownership_filter = this.collection.findWhere({ field: 'filter' });
        if (this.allowed_filters.ownership) {
            if (ownership_filter.get('value') == 'me') {
                this.allowed_filters.creator = false;
                this.allowed_filters.assignee = true;
                this.hide_team_filter();
            } else if (ownership_filter.get('value') == 'by_me') {
                this.allowed_filters.creator = true;
                this.allowed_filters.assignee = false;
                this.hide_team_filter();
            } else {
                this.allowed_filters.creator = true;
                this.allowed_filters.assignee = true;
                this.show_team_filter();
            }
        } else {
            this.show_team_filter();
        }

        this.render();
        return false;
    },

    _reshow_modal: function () {
        if (!this.isClosed) {
            this.render();
            return;
        }

        // Since BaseTaskProjectFilter is a modal and we're showing a modal, we won't be taken
        // back here for free. Instead, we need to manually show ourselves again.
        $f.mobile.nav.show_modal(this.constructor, this.options);
    },

    show_team_selector_modal: function (event) {
        var this_ = this;
        if ($(event.target).hasClass('disabled')) {
            return false;
        }
        $f.mobile.nav.show_modal(SelectDistributeGroupModal, {
            post_on_select: function (model) {
                this_.collection
                    .findWhere({ field: 'group_id' })
                    .set({ value: model.get('id'), display_value: model.get('name') });
                this_._reshow_modal();
            },
            on_clear: function () {
                this_.clear_team_filter();
            }
        });

        return false;
    }
});

// @author:david eventually we should move this to tasks/views.js
export const TaskFilter = BaseTaskProjectFilter.extend({
    template: '#tpl_task_filter',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_filter_tasks');
    },
    className: 'task_filter_page',
    allowed_filters: null, // pass in a list of filters to show

    templateHelpers: function () {
        var team_filter = this.collection.findWhere({ field: 'group_id' });
        var account_filter = this.collection.findWhere({ field: 'account_id' });
        var status_filter = this.collection.findWhere({ field: 'status' });
        var filter_filter = this.collection.findWhere({ field: 'filter' });
        var sort_options = this.collection.findWhere({ field: 'order_by' });
        var group_by_options = this.collection.findWhere({ field: 'group_by' });
        var search_query = this.collection.findWhere({ field: 'search_query' }).get('value');

        if (this.allowed_filters && this.allowed_filters.ownership) {
            if (filter_filter.get('value') == 'me') {
                this.allowed_filters.creator = false;
            } else if (filter_filter.get('value') == 'by_me') {
                this.allowed_filters.assignee = false;
            }
        }

        if (this.allowed_filters && this.allowed_filters.status) {
            const statusFilterValue = status_filter.get('value');
            if (statusFilterValue === 'closed' || statusFilterValue === 'archived_incomplete') {
                this.allowed_filters.sort = false;
            } else {
                this.allowed_filters.sort = true;
            }
        }

        return {
            account_filter_name: account_filter.get('display_value'),
            team_filter_name: team_filter.get('display_value'),
            status_filter_options: _.map(status_filter.get('select_options'), function (option) {
                return _.extend(option, { selected: status_filter.get('value') == option.value ? 'selected' : '' });
            }),
            filter_filter_options: _.map(filter_filter.get('select_options'), function (option) {
                return _.extend(option, { selected: filter_filter.get('value') == option.value ? 'selected' : '' });
            }),
            sort_options:
                sort_options &&
                _.map(sort_options.get('select_options'), function (option) {
                    return _.extend(option, { selected: sort_options.get('value') == option.value ? 'selected' : '' });
                }),
            group_by_options: _.map(group_by_options.get('select_options'), function (option) {
                return _.extend(option, { selected: group_by_options.get('value') == option.value ? 'selected' : '' });
            }),
            allowed_filters: this.allowed_filters,
            has_search_query: search_query && search_query !== ''
        };
    },

    initialize: function () {
        TaskFilter.__super__.initialize.apply(this, arguments);
        this.collection.set_valid_group_by_filter();
    },

    events: $.extend(
        {
            'click .pagelist .account_filter': 'show_account_selector_modal',
            'click .pagelist .clear_account_filter': 'clear_account_filter'
        },
        BaseTaskProjectFilter.prototype.events
    ),

    clear_account_filter: function (event) {
        this.collection.findWhere({ field: 'account_id' }).set({
            display_value: '',
            value: null
        });

        this.collection.set_valid_group_by_filter();
        this.render();
        return false;
    },

    show_account_selector_modal: function (event) {
        var this_ = this;

        if ($(event.target).hasClass('disabled')) {
            return false;
        }

        $f.mobile.nav.show_modal(SelectAccountModal, {
            post_on_select: function (model) {
                this_.collection
                    .findWhere({ field: 'account_id' })
                    .set({ value: model.get('id'), display_value: model.get('name') });
                this_.collection.set_valid_group_by_filter();
                this_._reshow_modal();
            },

            on_clear: function () {
                this_.collection.set_valid_group_by_filter();
                this_.clear_account_filter();
            }
        });

        return false;
    }
});

export const TasksFilteredByProjectFilter = TaskFilter.extend({
    template: '#tpl_tasks_filtered_by_project_filter',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_filter_project_tasks');
    }, // we'll set this once we know it
    className: 'tasks_filtered_by_project_page',
    toolbarnav_center_title: true
});

export const DeviceItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_device_item',
    tagName: 'li',

    templateHelpers: function () {
        return {
            is_selected: $f.mobile.bluetooth.devices.selected_device === this.model
        };
    },

    events: {
        'click .device_btn': 'select_device'
    },

    _remove_discovered_class_timeout: function () {
        if (this._remove_discovered_class_id) {
            clearTimeout(this._remove_discovered_class_id);
            this._remove_discovered_class_id = null;
        }
    },

    on_discovered: function () {
        var this_ = this;

        this._remove_discovered_class_timeout();

        var device_btn = this.$el.find('.device_btn');
        device_btn.addClass('discovered');

        this._remove_discovered_class_id = setTimeout(function () {
            device_btn.removeClass('discovered');
            this_._remove_discovered_class_id = null;
        }, 500);
    },

    select_device: function (event) {
        if (this.$el.find('.checked').length) {
            this.model.collection.unselect_device();
        } else {
            this.model.collection.select_device(this.model);
        }

        this.trigger('selected_device_changed');
        return false;
    },

    onClose: function () {
        this._remove_discovered_class_timeout();
    }
});

export const DevicesList = Backbone.Marionette.CollectionView.extend({
    tagName: 'ul',
    itemView: DeviceItem,
    className: 'pagelist',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        var this_ = this;
        this.on('itemview:selected_device_changed', function (view) {
            this_.render();
        });

        this.listenTo($f.application.vent, 'btdevice:device_discovered', function (device) {
            var deviceItem = this_.children.findByModel(device);
            if (!deviceItem) {
                return;
            }
            deviceItem.on_discovered();
        });
    }
});

/*
 *
 */
export const Devices = BaseToolbarNavPageLayout.extend({
    template: '#tpl_devices',

    toolbarnav_title: function () {
        return _t('mobile_settings_devices');
    },

    events: {
        'click .scan_btn:not(.scanning)': 'start_scan'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        this.$el.addClass('bluetooth_devices');
        this.start_scan();

        $f.application.vent.on('btdevice:ble_scan_stopped', function () {
            this_.$el.find('.scan_btn').removeClass('scanning');
        });

        $f.application.vent.on('btdevice:ble_scan_started', function () {
            this_.$el.find('.scan_btn').addClass('scanning');
        });
    },

    regions: {
        r_bluetooth_device_list: '.bluetooth_device_list'
    },

    onRender: function () {
        this.r_bluetooth_device_list.show(
            new DevicesList({
                collection: $f.mobile.bluetooth.devices
            })
        );
    },

    start_scan: function () {
        $f.mobile.bluetooth.enumerate_devices();
        return false;
    },

    onClose: function () {
        $f.application.vent.off('btdevice:ble_scan_stopped');
        $f.application.vent.off('btdevice:ble_scan_started');

        $f.mobile.bluetooth.stop_scan();
    }
});

/**
 * Our tasks list page
 *
 * @author:david eventually we should move this to tasks/views.js
 */
export const TasksPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_tasks',
    toolbarnav_title: function () {
        return $f.active_user.is_submitter() ? _t('mobile_tso_filter_my_tasks') : _t('mobile_common_tasks');
    },
    use_transitions: false,
    className: 'tasks',
    search_options: null, // TaskSearchOptions
    search_options_class: TaskSearchOptions,
    is_top_level_page: true,
    default_value: '',
    toolbarnav_toggle_search: false,
    allowed_filters: null, // default is set in view_tasks_list if not present here
    show_search: true,
    include_page_scrollable: true, // Some subclasses may not want this (e.g., for Dashboard)
    tasks_collection_class: MobileTasks,
    prioritize_cached_results: false,

    task_bucket_collections: null, // {} - either { [bucket_name] }, or { 'account-#': { [bucket_name] } }

    ACTIVE_SCROLLTOP_RESTORE_DELAY_MS: 100,

    // passed in data for dashboard cards if viewing from dash
    dashboard_cards: null,
    dashboard_tab_id: null,

    regions: {
        r_dashboard_cards: '.tasks_dashboard_cards_container',
        r_search_bar: '.search_bar',
        r_search_tasks: '.r_search_tasks',
        r_tasks_buckets: '.r_tasks_buckets'
    },

    events: {
        'click .filter_list': 'view_filter_tasks',
        'focus .search_bar .search': 'hide_filter_list',
        'blur .search_bar .search': 'show_filter_list'
    },

    templateHelpers: function () {
        var this_ = this;
        var is_filter_shown = function () {
            if (this_.allowed_filters === null) {
                return true;
            }
            return _.size(this_.allowed_filters) > 0;
        };

        return {
            allowed_filters: this.allowed_filters,
            is_filter_shown: is_filter_shown(),
            show_search: this.show_search,
            include_page_scrollable: this.include_page_scrollable
        };
    },

    /**
     * This is needed to handle history
     */
    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.task_bucket_collections) {
            this._reset_task_bucket_collections();
        }

        if ($f.mobile.nav.is_current_page_historic()) {
            // since we can be loaded inside of the dashboard, we don't always have this passed to us correctly
            // when $f.mobile.nav.go_back() is called.
            this.is_historic_view = true;
        }

        // Select leftnav as active if on main tasks page
        if (this.is_top_level_page) {
            $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_tasks');
        }
    },

    onRender: function () {
        if (this._isShown) {
            this._on_show_render();
        }
    },

    _reset_task_bucket_collections: function () {
        this.task_bucket_collections = {};
        $f.mobile.nav.add_options({ task_bucket_collections: this.task_bucket_collections });
    },

    /**
     * Runs only on show and successive renders (not on render before show)
     *
     * @todo:marionette_showrender @jira:INF-559
     */
    _on_show_render: function () {
        var this_ = this;

        this._get_search_options(function (search_options) {
            search_options.set_valid_group_by_filter();
            this_._show_search_bar(search_options);
            this_._show_active_filter_string(search_options);
            this_._show_dashboard_cards(search_options);

            if (this_.default_value) {
                this_.search(this_.default_value);
                this_._show_task_search_results(this_.task_bucket_collections.search);
            } else {
                this_._show_bucket_sets(); // in _on_show_render() so buckets have .page_scrollable element available
            }
        });
    },

    onShow: function () {
        TasksPage.__super__.onShow.call(this);

        this.on_orientation_change(); // Resize the page_scrollable area for search bar changes
        this._on_show_render();
        this._restore_active_scrolltop();
    },

    _restore_active_scrolltop: function () {
        setTimeout(function () {
            $f.mobile.nav.restore_scroll_position();
        }, this.ACTIVE_SCROLLTOP_RESTORE_DELAY_MS);
    },

    _show_bucket_sets: function () {
        var this_ = this;

        this._get_search_options(function (search_options) {
            var group_by_search_option = search_options.findWhere({ field: 'group_by' });
            if (group_by_search_option && group_by_search_option.get('value') === 'account') {
                this_._show_account_buckets(search_options);
            } else {
                import('./tasks/views').then((module) => {
                    this_.r_tasks_buckets.show(
                        new module.TasksBucketSet({
                            task_bucket_collections: this_.task_bucket_collections,
                            search_options: search_options,
                            tasks_collection_class: this_.tasks_collection_class
                        })
                    );
                });
            }
        });
    },

    _show_account_buckets: function (search_options) {
        var this_ = this;

        var distribute_group_id = search_options.findWhere({ field: 'group_id' });
        var account_collection = new AccountsForTaskBuckets([], {
            distribute_group_id: distribute_group_id ? distribute_group_id.get('value') : null
        });

        showLoadingDialog();
        account_collection.fetch({
            prefer_offline: Boolean(Object.keys(this_.task_bucket_collections).length),
            success: function () {
                var no_location = new Account({ id: 'null', name: _t('mobile_tasks_bucket_no_location') });
                account_collection.add(no_location);
                import('./tasks/views').then((module) => {
                    this_.r_tasks_buckets.show(
                        new module.TasksAccountBucketList({
                            collection: account_collection,
                            task_bucket_collections: this_.task_bucket_collections,
                            search_options: search_options,
                            tasks_collection_class: this_.tasks_collection_class
                        })
                    );
                    hideLoadingDialog();
                });
            },
            error: function () {
                hideLoadingDialog();
            }
        });
    },

    _get_search_options: function (callback) {
        var this_ = this;

        if (this.search_options) {
            callback(this.search_options);
            return;
        }

        var search_options = new this.search_options_class(null);

        search_options.fetch_from_local(function () {
            this_.search_options = search_options;
            this_.listenTo(this_.search_options, 'filters_updated', function () {
                this_._reset_task_bucket_collections();
                this_.render();
            });

            callback(this_.search_options);
        });
    },

    _get_collection_for_text_search: function (search_options) {
        if (this.task_bucket_collections.search) {
            return this.task_bucket_collections.search;
        }

        this.task_bucket_collections.search =
            this.tasks_collection_class.create_collection_for_text_search(search_options);

        return this.task_bucket_collections.search;
    },

    view_filter_tasks: function () {
        // we may not have fetched our search_options from local yet, so make sure we have them before we try to show filters
        if (!this.search_options) {
            return false;
        }

        // if nothing is passed in, this is the main page. show all filters.
        const allowed_filters = this.allowed_filters || {
            team: true,
            location: true,
            ownership: true,
            assignee: true,
            creator: true,
            status: true,
            group_by: true,
            sort: true
        };

        $f.mobile.nav.show_modal(TaskFilter, {
            collection: this.search_options,
            allowed_filters: allowed_filters
        });

        return false;
    },

    // hide_filter_list and show_filter_list are to make it easier to view search results
    // on mobile without having to hide the keyboard to see results.
    hide_filter_list: function () {
        if (!isWeb()) {
            $('.active_filters').hide();
            $('.filter_list').hide();
        }
    },

    show_filter_list: function () {
        if (!isWeb()) {
            $('.active_filters').show();
            $('.filter_list').show();
        }
    },

    _show_dashboard_cards: function () {
        if (!this.dashboard_cards) {
            return;
        }

        import('./dashboard/views').then((module) => {
            this.r_dashboard_cards.show(
                new module.MobileDashboardCards({
                    collection: this.dashboard_cards,
                    tab_id: this.dashboard_tab_id
                })
            );
        });
    },

    _show_search_bar: function (search_options) {
        var this_ = this;
        var search_query_value = search_options.findWhere({ field: 'search_query' }).get('value');

        // Set our default value and default search to search_options if any
        if (search_query_value != null) {
            this.default_value = search_query_value;
        }

        this.r_search_bar.show(
            new MobileSearchBar({
                collection: this_._get_collection_for_text_search(search_options),
                default_value: this_.default_value,

                on_search_clear: function () {
                    this_.clear_search();
                },
                search: function (query, callback) {
                    this_.search(query, callback);
                }
            })
        );
    },

    /**
     * Since we don't always have search_options ready when rendering, we will inject the description into HTML
     * instead of printing it in the template.
     */
    _show_active_filter_string: function (search_options) {
        if (search_options.get_filter_string()) {
            const formatted_filter = _.template('<%- filter %>');
            this.$el.find('.active_filters').html(formatted_filter({ filter: search_options.get_filter_string() }));
        }
    },

    _show_task_search_results: function (results) {
        import('./tasks/views').then((module) => {
            this.r_search_tasks.show(
                new module.TasksBucket({
                    collection: results,
                    no_fetch: true // collection will fetch itself when searching
                })
            );
            this._hide_bucket_sets();
        });
    },

    /**
     * actual searching logic and rendering of the search results
     * @param {string} query Passed in from search_keypress
     */
    search: function (query) {
        var this_ = this;
        if (!query || query === '') {
            this.clear_search();
            this.r_search_tasks.close();
            this._show_bucket_sets();
            return;
        }

        if (
            query === this.search_options.get_value_for_field('search_query') &&
            this.task_bucket_collections.search.is_fetched
        ) {
            // don't repeat this search, we already have results
            this_._show_task_search_results(this.task_bucket_collections.search);
            return;
        }

        this.task_bucket_collections.search.reset();

        showLoadingDialog();
        this.task_bucket_collections.search.typeahead_search(query, function (results) {
            hideLoadingDialog();
            var search_options = this_.search_options;
            this_.default_value = query;
            search_options.findWhere({ field: 'search_query' }).set('value', query);
            this_._show_active_filter_string(search_options);
            this_._show_task_search_results(results);
        });
    },

    _hide_bucket_sets: function () {
        this.r_tasks_buckets.close();
    },

    clear_search: function () {
        this.search_options.findWhere({ field: 'search_query' }).set('value', null);
        this._show_active_filter_string(this.search_options);
        this.default_value = '';
    }
});

// View of tasks filtered by project
export const TasksFilteredByProject = TasksPage.extend({
    toolbarnav_title: function () {
        return _t('mobile_common_projects');
    },
    is_top_level_page: false,
    className: 'tasks_filtered_by_project',
    search_options: null, // ProjectTaskSearchOptions,
    search_options_class: ProjectTaskSearchOptions,

    view_filter_tasks: function () {
        $f.mobile.nav.add_options();

        // we may not have fetched our search_options from local yet, so make sure we have them before we try to show filters
        if (!this.search_options) {
            return false;
        }

        $f.mobile.nav.show_modal(TasksFilteredByProjectFilter, {
            collection: this.search_options
        });

        return false;
    }
});

/**
 * A single row of the project list page.
 */
export const ProjectItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_project_item',
    model: null, // MobileProject
    tagName: 'li',

    templateHelpers: function () {
        return {
            relative_date_due: this.model.get('date_due')
                ? $f.utils.get_relative_date_from_today(new Date(this.model.get('date_due').$date))
                : null,
            relative_date_start: this.model.get('date_start')
                ? $f.utils.get_relative_date_from_today(new Date(this.model.get('date_start').$date))
                : null,
            is_recurring: this.model.get('parent_project') && this.model.get('parent_project').recurring != null,
            time_due: this.model.get_time_start_and_due_str(),
            tasks_remaining: this.model.get('num_tasks') - this.model.get('num_tasks_closed')
        };
    },

    events: {
        'click .navlink': 'navigate_to_task_list'
    },

    navigate_to_task_list: function (event) {
        // Sends the currently selected search options to the task page and
        // navigates the user to the task list view
        var this_ = this;
        if (!this.model.get('is_started')) {
            return false;
        }

        var search_options = new ProjectTaskSearchOptions(null, {
            persist_on_change: false // don't save every time we set a value. Instead, we'll call save_local() once at the end
        });

        search_options.findWhere({ field: 'group_id' }).set({
            value: this.model.collection.paginator.get('group_id'),
            display_value: this.model.collection.group_name
        });
        search_options.findWhere({ field: 'status' }).set({ value: 'all' });
        search_options.findWhere({ field: 'is_active' }).set({ value: this.model.get('is_active') });
        search_options.findWhere({ field: 'filter' }).set({ value: this.model.collection.paginator.get('filter') });
        search_options
            .findWhere({ field: 'project_ids' })
            .set({ value: this.model.get('id'), display_value: this.model.get('title') });

        // Save filters to local storage and navigate to task list
        search_options.save_local(function () {
            $f.mobile.nav.show(TasksFilteredByProject, {
                toolbarnav_title: this_.model.get('title')
            });
        });
    }
});

export const ProjectFilter = BaseTaskProjectFilter.extend({
    template: '#tpl_project_filter',
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_filter_projects');
    },
    className: 'project_filter_page',

    templateHelpers: function () {
        var team_filter = this.collection.findWhere({ field: 'group_id' });
        var ownership_filter = this.collection.findWhere({ field: 'filter' });
        var is_active_filter = this.collection.findWhere({ field: 'is_active' });
        var type_filter = this.collection.findWhere({ field: 'type' });

        return {
            team_filter_name: team_filter.get('display_value'),
            ownership_filter_options: _.map(ownership_filter.get('select_options'), function (option) {
                return _.extend(option, { selected: ownership_filter.get('value') === option.value ? 'selected' : '' });
            }),
            active_filter_options: _.map(is_active_filter.get('select_options'), function (option) {
                return _.extend(option, { selected: is_active_filter.get('value') === option.value ? 'selected' : '' });
            }),
            type_filter_options: _.map(type_filter.get('select_options'), function (option) {
                return _.extend(option, { selected: type_filter.get('value') === option.value ? 'selected' : '' });
            })
        };
    }
});

export const MobileProjectsList = BaseToolbarNavList.extend({
    collection: null, // MobileProjects
    itemView: ProjectItem,
    className: 'projects_list',

    // If we're offline, our EmptyView is an offline message. Otherwise, it's a no projects message.
    getEmptyView: function () {
        return EmptyListMessage.extend({
            icon_class: 'df-success',
            i18n_key_message: 'mobile_empty_list_message_no_projects'
        });
    },

    initialize: function (options) {
        if (!this.collection) {
            this.collection = new MobileProjects();
        }
    }
});

/**
 * Our projects list page
 */
export const ProjectsView = BaseToolbarNavPageLayout.extend({
    template: '#tpl_projects',
    default_search_value: '',
    toolbarnav_title: function () {
        return _t('mobile_common_projects');
    },
    toolbarnav_toggle_search: false,
    use_transitions: false,
    className: 'projects',
    search_options: null, // ProjectSearchOptions
    is_top_level_page: true,

    _fetched_from_local: false, // have we yet fetched our search options from local yet?

    regions: {
        r_projects_list: '.projects_list_container',
        r_search_bar: '.search_bar'
    },

    events: {
        'click .filter_list': 'view_filter_projects',
        'click .projects_list li': 'navigate_to_task_list'
    },

    /**
     * This is needed to handle history
     */
    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        // We should show an actual back button and use a slide-in animation if we're not being shown from the leftnav
        if (!$f.mobile.nav.is_current_view_top_level_page()) {
            this.is_top_level_page = false;
            this.use_transitions = true;
        }
    },

    onRender: function () {
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_projects');
        var this_ = this;

        if (!this_.search_options) {
            this_.search_options = new ProjectSearchOptions(null);
        }

        // Set our default search value to search_options if any
        if (this.search_options && this.search_options.findWhere({ field: 'search_query' }).get('value')) {
            this.default_search_value = this.search_options.findWhere({ field: 'search_query' }).get('value');
        }

        // If we've already done so, don't grab the search options from local storage. This prevents
        // timing issues when we fail to get location, change to 'by date', and immediately re-render
        //
        // (This could be handled by placing our re-render in a callback after we've updated our search options, but
        // we are using events to sync to local storage every time search_options changes, not an explicit save_local() command.
        // @see our error callback in _show_task_list()
        if (this_._fetched_from_local) {
            this_.view_projects_list();
            return;
        }

        this_.search_options.fetch_from_local(function () {
            this_._fetched_from_local = true;

            // now that we have accurate search options, re-render so toolbarnav shows search option data
            // when we come back around, we shouldn't reach here again because _fetched_from_local is true
            this_.render();
        });
    },

    view_projects_list: function () {
        // Otherwise, show list that requires connection
        var paginator_options = {};
        this.search_options.each(function (option) {
            return (paginator_options[option.get('field')] = option.get('value'));
        });

        // @note @author:david if our filter is 'me' (that is, we're searching for just the current user's projects),
        // we will ignore the 'group_id' search option. We do this in Tasks.prototype.url() so
        // it applies for both web and mobile.

        this.show_projects_list(
            new MobileProjects([], {
                group_name: this.search_options.findWhere({ field: 'group_id' }).get('display_value')
            }),
            { force_request_gps_permission: true, paginator_options: paginator_options }
        );
    },

    view_filter_projects: function () {
        $f.mobile.nav.add_options();

        // we may not have fetched our search_options from local yet, so make sure we have them before we try to show filters
        if (!this.search_options) {
            return false;
        }

        $f.mobile.nav.show(ProjectFilter, {
            collection: this.search_options
        });
        return false;
    },

    /**
     * Show a list of projects using a specified collection and collectionView class. This will
     * try to check for a device location when fetching for projects.
     *
     * @param  {Collection} collection                              The collection to fetch() and show in the view_class
     * @param  {dict}       options                                 Some options for fetching GPS, etc.
     * @param  {boolean}    options.force_request_gps_permission    Whether or not to force a geolocation request (@see $f.mobile.get_device_location)
     */
    show_projects_list: function (collection, options) {
        options = options || {};
        options = $.extend(
            {
                force_request_gps_permission: false
            },
            options
        );

        if (this.r_projects_list && this.r_projects_list.currentView) {
            this.r_projects_list.close();
        }

        var this_ = this;

        var _show = function (_coll, fetch_failed) {
            hideLoadingDialog();
            this_.r_projects_list.show(new MobileProjectsList({ collection: collection }));
        };

        var _error = function () {
            hideLoadingDialog();
            this_.r_projects_list.show(
                new EmptyListMessage({
                    icon_class: 'df-alert',
                    i18n_key_message: 'mobile_empty_list_message_connection_error'
                })
            );
        };

        this_.r_search_bar.show(
            new MobileSearchBar({
                collection: collection,
                default_value: this_.default_search_value,
                on_search_clear: function () {
                    this_.clear_search();
                },
                search: function (query, callback) {
                    this_.search(query, callback);
                }
            })
        );

        // Fill in the filter description
        if (this_.search_options.get_filter_string()) {
            this_.$el.find('.active_filters').html(this_.search_options.get_filter_string());
        }

        collection.paginator.reset_pagination();
        collection.paginator.set(options.paginator_options);

        showLoadingDialog();

        // If offline, just show
        if (!isConnected()) {
            _show();
            return;
        }

        collection.fetch({ success: _show, error: _error });
    },

    /**
     * actual searching logic and rendering of the search results
     * @param {string} query Passed in from search_keypress
     */
    search: function (query, callback) {
        callback || (callback = function () {});
        if (!query) {
            query = '';
        }

        var collection = this.r_projects_list.currentView.collection;
        showLoadingDialog();
        collection.typeahead_search(query, function (results) {
            hideLoadingDialog();
            callback();
        });

        this.default_search_value = query;
        this.search_options.findWhere({ field: 'search_query' }).set('value', query);
    },

    clear_search: function (event) {
        this.search_options.findWhere({ field: 'search_query' }).set('value', null);
        this.default_search_value = '';
    }
});

export const SelectTagModal = BaseTypeaheadModal.extend({
    collection: new AccountTags(),
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_tag');
    },

    model: null, // AccountAndTag

    format_result: function (model) {
        return 'Tag: ' + model.get('name');
    },

    /*
     * Filter out the ALL locations tag (defined by is_editable=false currently)
     */
    transform_collection: function (results) {
        return results.filter(function (model) {
            return model.get('is_editable') === true;
        });
    }
});

export const SelectUserModal = BaseTypeaheadModal.extend({
    collection: null, // UsersDistributeGroupsAndRoles
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_user');
    },
    allow_teams: false,
    allow_users: true,

    initialize: function (options) {
        // not calling super on this initialize, because we need to both initialize this based on options which is what the parent does
        // but also set the collection based on this, which the super parent assumes will have already occurred or it raises an exception
        options = options || {};
        $.extend(this, options);
        this.collection = new UsersDistributeGroupsAndRoles([], {
            allow_all_company_group: this.allow_teams
        });

        SelectUserModal.__super__.initialize.apply(this, arguments);
    },

    format_result: function (item) {
        return item.get('name');
    },

    transform_collection: function (results) {
        var this_ = this;
        var results_array = results.filter(function (model) {
            if (!this_.allow_teams && model.get('type') == 'group') {
                return false;
            }
            if (!this_.allow_users && model.get('type') == 'user') {
                return false;
            }
            return true;
        });

        return new UsersDistributeGroupsAndRoles(results_array);
    }
});

export const SelectUsersModal = SelectUserModal.extend({
    allow_multiple: true,
    collection_class: UsersDistributeGroupsAndRoles
});

export const SelectFormTemplateModal = BaseTypeaheadModal.extend({
    collection: new FormTemplates(),
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_template');
    },

    initialize: function (options) {
        SelectFormTemplateModal.__super__.initialize.apply(this, arguments);
        this.collection.paginator.set({ filter: this.get_filter(options.is_create_context) });
    },
    get_filter: function (is_create_context) {
        // Admin can always see all
        if ($f.active_user.is_admin()) {
            return 'all';
        }
        // Manager can only see all if not in create context
        // Can see all on dashboard views
        // Cannot see all when creating a new project
        if ($f.active_user.is_manager() && !is_create_context) {
            return 'all';
        }

        return 'me';
    }
});

export const TeamMultiSelectModal = BaseTypeaheadModal.extend({
    collection_class: DistributeGroups,
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_team');
    },
    format_result: function (item) {
        return item.get('name');
    },
    allow_multiple: true
});

export const SelectDistributeGroupModal = BaseTypeaheadModal.extend({
    collection: new DistributeGroups(),
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_team');
    },

    format_result: function (item) {
        return item.get('name');
    },

    post_on_select: function (model) {
        return model;
    }
});

export const RolesMultiSelectModal = BaseTypeaheadModal.extend({
    collection_class: MobileUserRoles,
    toolbarnav_title: function () {
        return _t('mobile_placeholder_user_select_role');
    },
    format_result: function (item) {
        return item.get('name');
    },
    get_modal_options: function () {
        var super_options = RolesMultiSelectModal.__super__.get_modal_options.apply(this, arguments);
        $.extend(super_options, this.extra_modal_options);
        return super_options;
    },
    allow_multiple: true
});

export const SelectAccountOrGroupModal = BaseTypeaheadModal.extend({
    collection: new AccountsAndDistributeGroups(),
    toolbarnav_title: function () {
        return _t('mobile_toolbarnav_title_select_team_or_location');
    },

    format_result: function (item) {
        return item.get('name');
    },

    post_on_select: function (model) {
        return model;
    },

    // Overwriting this so we don't close the modal before we fetch the selected model
    on_select: function (model) {
        var this_ = this;

        // If a group is selected we can get the full model from the global distribute_groups
        if (model.get('type') === 'group') {
            $f.mobile.dashboard.active_search_item_name = model.get('name');
            this.post_on_select($f.mobile.dashboard.distribute_groups.get(model.get('raw_id')));
            this.close();
            return false;
        }

        // If an account is selected we need to fetch the full model
        if (model.get('type') === 'account') {
            var account = new Account({ id: model.get('raw_id') });
            showLoadingDialog();
            account.fetch({
                success: function () {
                    hideLoadingDialog();
                    $f.mobile.dashboard.active_search_item_name = account.get('name');
                    this_.post_on_select(account);
                    this_.close();
                },
                error: function () {
                    showErrorMessage(_t('mobile_message_task_detials_error_view_account'));
                    $f.mobile.dashboard.active_search_item_name = null;
                    this_.post_on_select(null);
                    this_.close();
                }
            });
        }
    }
});

export const AccountTypeahead = MobileBaseTypeahead.extend({
    template: '#tpl_zp_account_autocomplete',
    model: null, // Account
    modal_class: SelectAccountModal,
    i18n_key_placeholder: 'mobile_placeholder_account_location_name',
    display_address: false,

    templateHelpers: function () {
        var super_helpers = AccountTypeahead.__super__.templateHelpers.apply(this, arguments);
        super_helpers || (super_helpers = {});
        super_helpers.display_address = this.display_address;
        if (this.model.get('address')) {
            super_helpers.display_address = this.display_address;
        } else {
            super_helpers.display_address = false;
        }

        return super_helpers;
    },

    get_modal_options: function () {
        var super_options = AccountTypeahead.__super__.get_modal_options.apply(this, arguments);
        $.extend(super_options, {
            show_add_task: false // don't allow them to add a new account here
        });
        return super_options;
    },

    get_default_value: function () {
        return this.model.get('name');
    }
});

export const TagTypeahead = MobileBaseTypeahead.extend({
    model: null, // AccountAndTag
    modal_class: SelectTagModal,
    i18n_key_placeholder: 'mobile_placeholder_tag_filter_by',

    get_default_value: function () {
        return this.model.get('name');
    }
});

export const UsersTypeahead = MobileBaseTypeahead.extend({
    model: null, // User
    modal_class: SelectUserModal,
    i18n_key_placeholder: 'mobile_placeholder_user_type',
    extra_modal_options: {},

    html_name: 'user',

    get_default_value: function () {
        if (this.model instanceof User) {
            return this.model.get_display_name();
        } else {
            return this.model.get('name');
        }
    },

    get_modal_options: function () {
        var super_options = AccountTypeahead.__super__.get_modal_options.apply(this, arguments);
        $.extend(super_options, this.extra_modal_options);
        return super_options;
    }
});

export const FormTemplateTypeahead = MobileBaseTypeahead.extend({
    model: null, // FormTemplate
    modal_class: SelectFormTemplateModal,
    i18n_key_placeholder: 'mobile_task_tab_form',
    is_create_context: false, // whether or not this view is used to create a new task/project

    get_default_value: function () {
        return this.model.get('title');
    }
});

export const GenerateBatchEstimateCount = Backbone.Marionette.ItemView.extend({
    template: '#tpl_generate_batch_estimate_count',
    model: null, // BatchTask,
    _reestimate_timeout: null, // @see reestimate()

    initialize: function () {
        this.listenTo(this.model.get('user_role'), 'change', this.reestimate, this);
        this.listenTo(this.model.get('distribute_group'), 'change', this.reestimate, this);
        this.listenTo(this.model.get('account_tag'), 'change', this.reestimate, this);
    },

    templateHelpers: function () {
        var task_count = this.model.get('estimate_task_count');
        var task_count_string =
            task_count === 1 ? '1 ' + _t('mobile_common_task') : task_count + ' ' + _t('mobile_common_tasks');
        var user_count = this.model.get('estimate_user_count');
        var user_count_string =
            user_count === 1
                ? '1 ' + _t('mobile_batch_estimate_user')
                : user_count + ' ' + _t('mobile_batch_estimate_users');

        return {
            has_changed: this.has_changed,
            task_count_string: task_count_string,
            user_count_string: user_count_string
        };
    },

    reestimate: function () {
        var this_ = this;

        if (this._reestimate_timeout) {
            return;
        }

        if (!this.model.get('user_role').get('id') || !this.model.get('distribute_group').get('id')) {
            this.model.set({ estimate_task_count: null, estimate_user_count: null });
            return this.render();
        }

        this.render().$el.html($f.utils.get_loading_spinner_html());

        this._reestimate_timeout = setTimeout(function () {
            // allow a buffer space so back-to-back changes don't cause multiple requests
            clearTimeout(this_._reestimate_timeout);
            this_._reestimate_timeout = null;
            this_.model.fetch({
                success: function () {
                    this_.render();
                },
                error: function () {
                    this_.render();
                }
            });
        }, 300);
    }
});

export const UserRoleSelect = Backbone.Marionette.ItemView.extend({
    template: '#tpl_user_role_select',
    user_roles: null, // UserRoles
    model: null, // UserRole
    i18n_key_placeholder: 'mobile_placeholder_user_select_role',

    templateHelpers: function () {
        return {
            user_roles: this.user_roles,
            selected_id: this.model.get('id'),
            placeholder: _t(this.i18n_key_placeholder) || this.i18n_key_placeholder
        };
    },

    events: {
        'change select': 'bind_value'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    bind_value: function (event) {
        var target = $(event.target);
        this.model.set({ id: target.val(), name: target.attr('name') });
    }
});

export const AccountsView = SelectAccountModal.extend({
    is_top_level_page: true,
    is_modal: false,
    toolbarnav_title: function () {
        return _t('mobile_common_locations');
    },
    toolbarnav_center_title: false,
    toolbarnav_show_add_task: true,

    initialize: function (attributes, options) {
        AccountsView.__super__.initialize.apply(this, arguments);

        $f.mobile.nav.add_options({
            collection: this.collection
        });
    },

    onRender: function () {
        $f.application.vent.trigger('mobile:set_active_leftmenu_item', 'nav_locations');
    },

    on_select: function (model) {
        if (!isConnected()) {
            return showOfflineMessage();
        }

        // allow the active_tab_id to be saved
        $f.mobile.dashboard.active_tab_id = null;

        import('./dashboard/views').then((module) => {
            const { getDefaultLocationDashboardTabId, MobileDashboard } = module;
            $f.mobile.nav.show(MobileDashboard, {
                active_account_or_group: model,
                active_tab_id: getDefaultLocationDashboardTabId(),
                do_not_set_leftnav: true,
                reset_active_group_or_account_on_close: false
            });
        });
    }
});

export const ProjectCreator = BaseToolbarNavPageLayout.extend({
    template: '#tpl_project_creator',
    className: 'task_create_page',
    model: null, // MobileProject
    tasks_data: null, // BatchTask - subreferenced from this.model for ease of ease
    default_account: null, // Account - if specified, the task should be prefilled with this account
    use_transitions: true,
    toolbarnav_show_add_task: false, // don't need to show the add task button on the add task page
    toolbarnav_center_title: true,
    _saving: false,
    _has_changed: false, // tarcks if model changed

    toolbarnav_title: function () {
        return `${_t('mobile_task_button_create')} ${_t('mobile_task_button_project')}`;
    },

    events: {
        'change .watched_input': 'title_changed',
        'change .description_watched_input': 'description_changed',

        'click .save_project_details': 'create_project',
        'keyup textarea': 'expand_textarea',
        'click .auto_archive': 'set_auto_archive'
    },

    regions: {
        r_reply_tabs_for_task: '#reply_tabs_for_task',
        r_form_template_typeahead_for_task: '#form_template_typeahead_for_task',
        r_title_image_field: '.add_title_image_field',
        r_role_typeahead_for_task: '#role_typeahead_for_task',
        r_teams_typeahead_for_task: '#teams_typeahead_for_task',
        r_date_start_picker_for_task: '#date_start_picker_for_task',
        r_date_due_picker_for_task: '#date_due_picker_for_task',
        r_location_tags_for_task: '#location_tags_for_task',
        r_batch_generate_count: '#batch_generate_count'
    },

    templateHelpers: function () {
        return {
            reply: this.tasks_data.get('reply'),
            mode: this.mode
        };
    },

    initialize: function (options) {
        var this_ = this;
        options = options || {};
        $.extend(this, options);

        if (!this.model) {
            this.model = new MobileProject({
                id: $f.utils.local_guid(),
                local_guid: true,
                date_due: { $date: null },
                is_auto_archive: true
            });

            this.model.set(
                'create_tasks_data',
                new BatchTask(
                    {
                        reply: { type: 'quick' }
                    },
                    {
                        parent_project: this.model
                    }
                )
            );

            $f.mobile.nav.add_options({ model: this.model }); // when we come back, send us back our model
        }

        this.tasks_data = this.model.get('create_tasks_data');

        // Our regions depend on these sub-models being there:
        if (!this.tasks_data.get('assignee')) {
            this.tasks_data.set('assignee', {}, { silent: true });
        }
        if (!this.tasks_data.get('account_tags')) {
            this.tasks_data.set('account_tags', {}, { silent: true });
        }

        // change the 'reply', they'll lose their images. We should also be sure to clear them out manually when switching to "Submission" tasks.
        this.listenTo(this.tasks_data.get('reply'), 'change', function () {
            this_._has_changed = true;
            this_.render();
        });
        // When removing images on edit, we will need to update the title_image attribute from title_image_field so that
        // TaskDetails shows the edit even if the edited version hasn't finished uploading.
        this.model.store();

        this.listenTo(this.model, 'change', function () {
            this_._has_changed = true;
            this_.on_model_changed();
        });

        this.listenTo(this.tasks_data, 'change', function () {
            this_._has_changed = true;
            this_.on_model_changed();
        });

        // watch for file changes
        this.listenTo(this.tasks_data.get('title_image_field'), 'change:file', function () {
            this_._has_changed = true;
        });

        this.listenTo($f.application.vent, 'mobile:due_date_change', this.handle_due_date_change);
        this.listenTo($f.application.vent, 'mobile:start_date_change', this.handle_start_date_change);
    },

    onRender: function () {
        this.$el.find('textarea').trigger('keyup'); // trigger textarea_autoexpand if the reply type changes
        this.show_regions();
        this._initialize_auto_archive();
        this.bind_validation(); // we need to rebind validation to match the new model
        if (this._has_changed) {
            this.on_model_changed();
        }
        this.on_orientation_change();
    },

    // if  due date changes, make sure start date will be before it
    handle_due_date_change: function () {
        var $due_date_picker = this.$el.find('.date_due input');
        var $start_date_picker = this.$el.find('.date_start input');
        var new_start_date_max = $due_date_picker.val() ? new Date($due_date_picker.val()) : new Date();
        var current_start_date = $start_date_picker.val() ? new Date($start_date_picker.val()) : new Date();
        var min_date = moment().startOf('day').toDate();
        this.set_date_picker($start_date_picker, current_start_date, new_start_date_max, min_date);
    },

    // if start date changes, make sure due date will be after it
    handle_start_date_change: function () {
        var $due_date_picker = this.$el.find('.date_due input');
        var $start_date_picker = this.$el.find('.date_start input');
        var current_due_date = $due_date_picker.val() ? new Date($due_date_picker.val()) : new Date();
        var new_due_date_min = $start_date_picker.val() ? new Date($start_date_picker.val()) : new Date();
        var max_date = new Date(new Date().getFullYear() + 10, 11, 31);
        this.set_date_picker($due_date_picker, current_due_date, max_date, new_due_date_min);
    },

    // generic date picker setter
    set_date_picker: function (date_picker, default_value, max, min) {
        date_picker.mobiscroll().date({
            theme: 'material',
            display: 'bottom',
            defaultValue: default_value,
            max: max,
            focusOnClose: false,
            dateWheels: 'M D d yy',
            min: min
        });
    },

    show_regions: function () {
        var this_ = this;
        var user_roles = new UserRoles();

        this.r_reply_tabs_for_task.show(
            new TaskReplyTypeTabs({
                model: this.tasks_data.get('reply')
            })
        );

        // this shouldn't be re-rendered when the type is toggled or the image upload field is lost
        this.r_title_image_field.show(
            new ImageFieldView({
                model: this.tasks_data.get('title_image_field'),
                task: this.model,
                hide_caption: true
            })
        );

        var reply = this.tasks_data.get('reply');
        if (reply.get('type') == 'form_submission') {
            // Reply type is form_submission. Make sure we have a form_template model set:
            if (!reply.get('form_template')) {
                reply.set('form_template', new FormTemplate());
            }
            this.r_form_template_typeahead_for_task.show(
                new FormTemplateTypeahead({
                    is_create_context: true,
                    model: reply.get('form_template'),
                    post_on_select: function (raw_form_template) {
                        if (
                            reply &&
                            reply.get('form_template') &&
                            raw_form_template.get('id') == reply.get('form_template').get('id')
                        ) {
                            return;
                        }
                        // Always replace entire form template model so the reply's change event is fired. This would otherwise be taken care of if these were BaseZenputModels
                        reply.set(
                            'form_template',
                            new FormTemplate({ id: raw_form_template.id, title: raw_form_template.get('title') })
                        );
                        this_.model.set('title', this_.tasks_data.get('reply').get('form_template').get('title'));
                    },
                    on_clear: function () {
                        this_.tasks_data.get('reply').set('form_template', null);
                        this_.model.set('title', '');
                    }
                })
            );
        }

        this.r_date_start_picker_for_task.show(
            new DateDuePicker({
                i18n_key_placeholder: 'mobile_tso_filter_start_date',
                date_due: new Date(),
                picker_type: 'date_start',

                on_select: function (date) {
                    this_.set_date_start(date);
                    $f.application.vent.trigger('mobile:start_date_change');
                }
            })
        );

        this.r_date_due_picker_for_task.show(
            new DateDuePicker({
                i18n_key_placeholder: 'mobile_tso_filter_due_date',
                date_due: this.model.get('date_due'),
                on_select: function (date) {
                    this_.set_date_due(date);
                    $f.application.vent.trigger('mobile:due_date_change');
                }
            })
        );

        this.r_teams_typeahead_for_task.show(
            new UsersTypeahead({
                i18n_key_placeholder: 'mobile_placeholder_filter_by_team',
                extra_modal_options: {
                    allow_teams: true,
                    allow_users: false,
                    toolbarnav_title: function () {
                        return _t('mobile_toolbarnav_title_select_team');
                    }
                },
                html_name: 'distribute_group',
                model: this_.tasks_data.get('distribute_group'),
                post_on_select: function (raw_group) {
                    this_.model
                        .get('create_tasks_data')
                        .get('distribute_group')
                        .set({
                            id: raw_group.get('raw_id'),
                            name: raw_group.get('name')
                        });
                },
                on_clear: function () {
                    this_.tasks_data.get('distribute_group').clear();
                }
            })
        );

        this.r_location_tags_for_task.show(
            new TagTypeahead({
                model: this_.tasks_data.get('account_tag'),
                post_on_select: function (tag) {
                    this_.model
                        .get('create_tasks_data')
                        .get('account_tag')
                        .set({
                            id: tag.get('raw_id'),
                            name: tag.get('name')
                        });
                },
                on_clear: function () {
                    this_.tasks_data.get('account_tag').clear();
                }
            })
        );

        user_roles.fetch({
            success: function () {
                this_.r_role_typeahead_for_task.show(
                    new UserRoleSelect({
                        user_roles: user_roles,
                        model: this_.tasks_data.get('user_role'),
                        i18n_key_placeholder: 'mobile_placeholder_assign_to_user_role'
                    })
                );
            },
            error: function () {
                this_.$el.find('.roles_loading_msg').html(_t('mobile_html_failed_loading_roles'));
            }
        });

        this.r_batch_generate_count.show(
            new GenerateBatchEstimateCount({
                model: this.model.get('create_tasks_data')
            })
        );

        this.$el.find('.save_project_details').removeClass('disabled');
    },

    _initialize_auto_archive: function () {
        var value = this.model.get('is_auto_archive') ? 1 : 0;
        this.$el.find('[name="auto_archive_radio"][value="' + value + '"]').attr('checked', true);
    },

    bind_validation: function () {
        var this_ = this;
        Backbone.Validation.unbind(this);
        Backbone.Validation.bind(this);

        this.model.unbind('validated:invalid');
        this.model.bind('validated:invalid', function (model, error) {
            this_.$el.find('.form-field.invalid').removeClass('invalid');
            this_.$el.find('.invalid').parents('.form-field').addClass('invalid');
        });

        this.model.unbind('validated:valid');
        this.model.bind('validated:valid', function (model, error) {
            this_.$el.find('.invalid').removeClass('invalid');
        });
    },

    on_model_changed: function () {
        this.model.validate();
    },

    title_changed: function (event) {
        var target = $(event.target);
        this.model.set('title', target.val());
    },

    description_changed: function (event) {
        var target = $(event.target);
        this.model.set('description', target.val());
    },

    create_project: function (event) {
        if ($(event.target).hasClass('disabled')) {
            return false;
        }
        var reply = this.tasks_data.get('reply');

        // Keeps Reply valid
        if (reply.get('type') != 'form_submission') {
            reply.set('form_template', null);
        }

        if (reply.get('type') == 'form_submission' && !reply.get('form_template').id) {
            showErrorMessage(_t('mobile_message_task_creator_error_save_select_form'));
            return false;
        }

        if (!this.model.isValid(true) || !this.tasks_data.isValid(true)) {
            showErrorMessage(_t('mobile_message_task_creator_error_save_required_fields'));
            return false;
        }

        // this will fail on the backend json.loads if passed { $date: null }
        if (this.model.get('date_due') && !this.model.get('date_due').$date) {
            this.model.set('date_due', null);
        }

        this.$el.find('.save_project_details').addClass('disabled');
        showLoadingDialog();

        this.save_project(event);

        return false;
    },

    reenable_save_button: function () {
        this.$el.find('.save_project_details').removeClass('disabled');
        hideLoadingDialog();
    },

    save_project: function (event) {
        var this_ = this;
        var estimate_tasks = this.tasks_data.get('estimate_task_count');

        if (estimate_tasks == undefined || estimate_tasks == 0) {
            showErrorMessage(_t('mobile_message_task_creator_error_save_batch_change'));
            this.reenable_save_button();
            return false;
        }

        $f.mobile.new_project.add_records(
            this.model,
            function () {
                this_.saving = true;
                this_.reenable_save_button();
                $f.mobile.uploads.start_upload({
                    error_msg: _t('mobile_message_task_detials_error_submit_task_project'),
                    success: function () {
                        $f.mobile.cache.void_ttl_by_cache_substring('mobile_tasks__');
                        $f.mobile.nav.go_back();
                    },
                    msg: _t('mobile_message_task_detials_wait_submit_task_created')
                });
            },
            function () {
                this_.reenable_save_button();
                showErrorMessage(_t('mobile_common_message_task_creator_error_save'), true);
            }
        );
    },

    set_auto_archive: function (event) {
        this.model.set('is_auto_archive', $(event.target).val() === '1');
    },

    set_date_start: function (date) {
        if (!date) {
            this.model.get('create_tasks_data').set('date_start', null);
            this.model.set('date_start_naive', null);
            return;
        }

        // ISO string, not tied to any TZ, to be interpreted locally, for the start of day
        var start_date = moment(date).utcOffset(0).startOf('day').toDate().toISOString().replace('Z', '');

        this.model.set('date_start_naive', start_date);
    },

    set_date_due: function (date) {
        if (!date) {
            this.model.get('create_tasks_data').set('date_due', null);
            this.model.set('date_due_naive', null);
            return;
        }

        // Instead of sending date_due directly, the serverside will generate it from date_due_naive,
        // which does not hold any timezone info.
        var date_due_naive = new Date(date).toLocaleDateString('en-CA') + 'T23:59:59.999'; // ISO string, not tied to any TZ, to be interpreted locally, for the end of day
        this.model.set('date_due_naive', date_due_naive);
    },

    _restore_model: function () {
        var this_ = this;

        this.model.restore();
        // Reset any image added to the title_image_field
        this.tasks_data.set(
            'title_image_field',
            new MediaFieldTask(
                {
                    id: 1,
                    type: 'image',
                    value: this_.tasks_data.get('title_image').toJSON() // loads the existing image urls into the field
                },
                {
                    task_or_project: this.tasks_data
                }
            ),
            { silent: true }
        );

        $f.mobile.nav.add_options({ model: this_.model });
        $f.mobile.nav.go_back();
    },

    go_back: function () {
        var this_ = this;

        if (this._saving || !this._has_changed) {
            return ProjectCreator.__super__.go_back.apply(this, arguments);
        }

        if (!isWeb()) {
            var options = {
                title: _t('mobile_projects_leaving_project'),
                subtitle: _t('mobile_message_task_detials_confirm_message'),
                position: [50, 60], // only used for iPad
                addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
            };

            if (isIos()) {
                options.buttonLabels = [_t('mobile_projects_save_project')];
                options.addDestructiveButtonWithLabel = _t('mobile_common_button_discard');
                options.destructiveButtonLast = true;
            } else if (isAndroid()) {
                options.buttonLabels = [_t('mobile_projects_save_project'), _t('mobile_common_button_discard')];
                options.androidTheme = CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT;
                options.androidEnableCancelButton = true;
            } else if (isWindows()) {
                options.buttonLabels = [_t('mobile_projects_save_project'), _t('mobile_common_button_discard')];
            }

            actionSheet(options, function (button_index) {
                if (button_index === 3) {
                    return;
                } // cancel
                if (button_index === 1) {
                    // save Task
                    this_.$el.find('.save_project_details').trigger('click');
                    return;
                }

                $f.mobile.uploads.clear_pending_file_uploads(this_.model.id);
                this_._restore_model();
            });
            return;
        }

        confirmNotification(
            _t('mobile_message_task_detials_confirm_message'),
            function (success) {
                if (!success) {
                    $f.mobile.uploads.clear_pending_file_uploads(this_.model.id);
                    this_._restore_model();
                    return;
                }

                this_.$el.find('.save_project_details').trigger('click');
            },
            _t('mobile_projects_leaving_project'),
            _t('mobile_projects_save_project'),
            _t('mobile_common_button_discard')
        );
    },

    expand_textarea: global_utils.expand_textarea
});

export const DateDuePicker = Backbone.Marionette.ItemView.extend({
    template: '#tpl_date_due_picker',
    date_due: null, // { $date: date }
    i18n_key_placeholder: 'mobile_common_placeholder_date',
    picker_type: 'date_due', // possible values: 'date_due', 'date_start'

    events: {
        'change input': 'save_value',
        'click input': 'show_picker'
    },

    templateHelpers: function () {
        return {
            formatted_date: this.format_date(),
            date_due: !this.date_due || !this.date_due.$date ? null : this.date_due.$date,
            placeholder: _t(this.i18n_key_placeholder) || this.i18n_key_placeholder,
            picker_type: this.picker_type
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    format_date: function () {
        if (!this.date_due || !this.date_due.$date) {
            return null;
        }
        var dt = new Date(this.date_due.$date);
        return moment(dt).format('ddd, MMM D, YYYY');
    },

    onBeforeRender: function () {
        if (this.$el.find('.date input')) {
            this.$el.find('.date input').mobiscroll('destroy');
        }
    },

    onRender: function () {
        // handle start date correctly when due date is set already
        var $due_date_picker = $('.date_due input');
        var max = new Date(new Date().getFullYear() + 10, 11, 31);

        if ($due_date_picker.val() && this.picker_type === 'date_start') {
            max = $due_date_picker.val();
        }

        this.$el
            .find('.date input')
            .mobiscroll()
            .date({
                theme: 'material',
                display: 'bottom',
                defaultValue: this.date_due ? new Date(this.date_due.$date) : new Date(),
                max: new Date(max),
                focusOnClose: false,
                dateWheels: 'M D d yy',
                min: moment().startOf('day').toDate()
            });
    },

    show_picker: function (event) {
        this.$el.find('input').mobiscroll('show');
        return false;
    },

    save_value: function (event) {
        var date = this.$el.find('.date input').mobiscroll('getDate').getTime(); // Get date from the date picker
        this.date_due.$date = moment(date).endOf('day').toDate(); // Set the received date to the end of the local day
        this.render();
        this.on_select(date);
    },

    on_select: function (date) {
        // to be subclassed
    }
});

export const TaskReplyTypeTabs = Backbone.Marionette.ItemView.extend({
    template: '#tpl_task_reply_tabs',
    model: null, // TaskReply

    events: {
        'click .reply_type i': 'update_task_reply_type',
        'click .reply_type a': 'update_task_reply_type'
    },

    update_task_reply_type: function (event) {
        if (event) {
            event.preventDefault();
        }
        var val = $(event.target).closest('div').data('type');
        this.model.set('type', val);
        this.render();
    }
});

export const TriggeringImageFieldsList = Backbone.Marionette.CollectionView.extend({
    collection: null, // GenericImageFields
    itemView: ImageFieldView,
    itemViewOptions: function (model) {
        // @author: kgstew quick hack to get this view to render
        // This Collection view is using a BaseField model which relies on being attached to a FormStructure collection
        // Instead we should be converting this field into a model appropriate for the TriggeringImageFieldsList

        return {
            hide_caption: !model.get('notes')
        };
    },

    onShow: function () {
        // copied functionality to display read only form fields from SubmittedFormPage.onShow
        this.$el.addClass('historical_submission');
        this.$el.find('textarea').attr('disabled', 'disabled');
        this.$el.find('textarea').attr('readonly', 'readonly');
        this.$el.find('select').attr('disabled', 'disabled');
    }
});

export const TaskDetailsPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_task_details_page',
    model: null, // MobileTask
    className: 'task_details_page',
    toolbarnav_title: null, // may be passed in with options
    use_transitions: true,
    tabs: [], // an array of f.mobile.views.TabbedLayout
    comments: null, // $f.collection.Comments
    auto_resize_page_scrollable: false, // our TabbedNav pages will handle this
    active_comment_id: null, // the comment ID to focus, if any

    regions: {
        r_tabbed_layout: '.task_details_tabbed_layout'
    },

    events: {
        'click .edit_task_action': 'click_edit_task_action',
        'click .delete_task_action': 'click_delete_task_action',
        'click .archive_task_action': 'click_archive_task_action'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.toolbarnav_title =
            this.toolbarnav_title ||
            function () {
                return _t('mobile_common_tasks');
            };

        this.tabs = [
            {
                id: 'task_info',
                name: _t('mobile_dashboard_tab_title_info'),
                view_class: TaskInfo,
                view_options: {
                    model: this.model
                }
            }
        ];
    },

    onShow: function () {
        var this_ = this;
        TaskDetailsPage.__super__.onShow.apply(this, arguments);

        showLoadingDialog();

        // Get the full task before we try to fetch comments
        if (!this.is_historic_view) {
            this.model.fetch({
                success: function () {
                    this_._fetch_comments();
                },
                error: function () {
                    this_._fetch_comments();
                }
            });
        } else {
            this._fetch_comments();
        }
    },

    _fetch_comments: function () {
        var this_ = this;
        this.fetch_comments({
            task: this_.model,
            success: async function () {
                hideLoadingDialog();

                await this_.add_comments_tab();
                this_._show_tabbed_layout();
                this_.on_orientation_change();
            },
            error: async function (_error) {
                hideLoadingDialog();

                await this_.add_comments_tab();
                this_._show_tabbed_layout();
            }
        });
    },

    on_toolbarnav_init: function () {
        // do not allow editing for already complete tasks
        if (this.model.is_fulfilled()) {
            return;
        }
        if (!this.model.user_can_edit($f.active_user)) {
            return;
        }
        var task_actions = [];
        var edit_task_action = {
            container_class: 'edit_task_action',
            icon_class: 'df-edit',
            i18n_key_text: 'mobile_common_button_edit'
        };
        var archive_task_action = {
            container_class: 'archive_task_action',
            icon_class: 'df-remove-circle',
            i18n_key_text: 'mobile_common_button_archive'
        };
        var delete_task_action = {
            container_class: 'delete_task_action',
            icon_class: 'df-remove-circle',
            i18n_key_text: 'mobile_drafts_button_delete'
        };
        if (!this.model.is_archived()) {
            task_actions.push(edit_task_action);
        }
        if (this.model.user_can_delete($f.active_user)) {
            task_actions.push(archive_task_action);
            task_actions.push(delete_task_action);
        }
        this.toolbarnav.add_actions(task_actions);
    },

    on_orientation_change: function () {
        TaskDetailsPage.__super__.on_orientation_change.apply(this, arguments);
    },

    go_back: function () {
        if (this.is_comments_tab()) {
            this.go_back_comments_tab();
            return;
        }

        var this_ = this;
        if (this.model.get('status').get('type') == 'closed') {
            $f.mobile.nav.go_back();
            return false;
        }

        if (this.model.get('reply').get('type') != 'quick') {
            $f.mobile.nav.go_back();
            return false;
        }

        var field = this.model.get('fulfillment').get('fields')[0];
        if (!field || (!field.has_value() && !field.get('notes'))) {
            $f.mobile.nav.go_back();
            return false;
        }

        confirmNotification(
            _t('mobile_message_task_detials_confirm_message'),
            function (success) {
                if (!success) {
                    return;
                }

                $f.mobile.uploads.clear_pending_file_uploads(this_.model.id);
                $f.mobile.nav.go_back();
            },
            _t('mobile_message_task_detials_confirm_title'),
            _t('mobile_common_button_yes'),
            _t('mobile_common_button_no')
        );

        return false;
    },

    click_edit_task_action: function (event) {
        var this_ = this;
        this.toolbarnav.hide_overflow_menu();

        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        $f.mobile.new_tasks.get_pending_task(this_.model.id, function (task) {
            if (task) {
                showErrorMessage(_t('mobile_task_edition_already_in_progress'));
                return;
            }

            showLoadingDialog();
            this_.model.fetch({
                timeout: 10000,
                success: function () {
                    hideLoadingDialog();
                    this_.model.set(
                        'title_image_field',
                        new MediaFieldTask(
                            {
                                id: 1,
                                type: 'image',
                                value: this_.model.get('title_image').toJSON() // loads the existing image urls into the field.
                            },
                            {
                                task_or_project: this_.model
                            }
                        ),
                        { silent: true }
                    );

                    this_.model.set('is_edit_mode', true);

                    import('./tasks/views').then((module) => {
                        $f.mobile.nav.show(module.TaskEditPage, {
                            model: this_.model,
                            mode: 'edit'
                        });
                    });
                },
                error: function () {
                    hideLoadingDialog();
                    showErrorMessage(_t('mobile_message_show_error_message'));
                }
            });
        });
        return false;
    },

    execute_task_action: function (event, opts) {
        var this_ = this;

        this.toolbarnav.hide_overflow_menu();

        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        var _success_callback = function () {
            showSuccessMessage(opts.success_message);
        };

        var _error_callback = function (response) {
            showErrorMessage(opts.error_message);
            this_.toolbarnav.hide_overflow_menu();
            $f.mobile.logging.log_error(opts.log_message, response);
        };

        var _go_to_tasks = function () {
            $f.mobile.nav.show(TasksPage, { prioritize_cached_results: true });
        };

        // Redefine the _destroy_model function for the 'delete' method.
        var _destroy_model = null;
        if (opts.method === 'delete') {
            _destroy_model = function () {
                this_.model.delete_from_collection_and_caches(function () {
                    _go_to_tasks();
                    this_.model.delete({
                        success: function () {
                            this_.model.trigger('destroy', this_.model, this_.model.collection);
                            _success_callback();
                        },
                        error: function (model, response) {
                            _error_callback(response);
                        }
                    });
                });
            };
        } else {
            _destroy_model = function () {
                // @author:david, putting this here instead of in success() to make sure we still have model.collection
                // before the model is destroyed.
                this_.model.mark_as_archived_in_caches(function () {
                    _go_to_tasks();
                    this_.model.destroy({
                        wait: true, // wait for the server to reply before removing model
                        success: function (model, response) {
                            _success_callback();
                        },
                        error: function (model, response) {
                            _error_callback(response);
                        }
                    });
                });
            };
        }

        this._confirm_archive_delete_action(opts, function () {
            _destroy_model();
        });

        return false;
    },

    click_archive_task_action: function (event) {
        this.execute_task_action(event, {
            success_message: _t('mobile_task_task_archived'),
            error_message: _t('mobile_task_error_archiving_task'),
            log_message: 'Closing task failed.',
            title_message: _t('mobile_task_archive_task'),
            button_labels_arr: [_t('mobile_task_archive_task')],
            confirm_message: _t('mobile_task_archive_task_confirm'),
            confirm_action_message: _t('mobile_task_archive_task'),
            method: 'close'
        });

        return false;
    },

    click_delete_task_action: function (event) {
        this.execute_task_action(event, {
            success_message: _t('mobile_task_task_deleted'),
            error_message: _t('mobile_task_error_deleting_task'),
            log_message: 'Deleting task failed.',
            title_message: _t('mobile_tasks_delete_task'),
            button_labels_arr: [_t('mobile_tasks_delete_task')],
            confirm_message: _t('mobile_task_delete_task_confirm'),
            confirm_action_message: _t('mobile_tasks_delete_task'),
            method: 'delete'
        });

        return false;
    },

    /**
     * Confirm with the user that we should proceed with archiving/deleting the task when requested.
     *
     * @todo @jira:ZP-511 this needs to be further cleaned up globally
     */
    _confirm_archive_delete_action: function (opts, success) {
        if (isNative()) {
            var options = {
                title: opts.title_message,
                subtitle: opts.confirm_message,
                buttonLabels: opts.button_labels_arr,
                position: [50, 60], // only used for iPad
                addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
            };

            if (isAndroid()) {
                options.androidTheme = CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT;
                options.androidEnableCancelButton = true;
            }

            actionSheet(options, function (button_index) {
                if (button_index === 2) {
                    return;
                } // cancel

                success();
            });

            return;
        }

        confirmNotification(
            opts.confirm_message,
            function (is_success) {
                if (!is_success) {
                    return;
                }

                success();
            },
            opts.confirm_action_message,
            _t('mobile_common_button_yes'),
            _t('mobile_common_button_no')
        );
    },

    _show_tabbed_layout: function () {
        if (!this.r_tabbed_layout) {
            return; // view must have closed
        }

        this.r_tabbed_layout.show(
            new TabbedLayout({
                is_tabs_full_width: true,
                hide_tabs: false,
                tab_pages: new TabbedLayoutPageList(this.tabs),
                active_tab_id: this.active_comment_id ? $f.shared.comments.utils.COMMENTS_TAB_ID : null
            })
        );
    }
});

Cocktail.mixin(TaskDetailsPage, CommentsPageLayoutMixin);

export const TaskInfo = BaseTabbedLayoutPage.extend({
    template: '#tpl_task_info',
    model: null, // MobileTask

    regions: {
        r_title_images: '.title_images',
        r_task_image_field: '.form_container',
        r_triggering_image_fields: '.triggering_image_fields',
        r_cooldown_stages: '.cooldown_stages'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.model.set_default_fulfillment();
        this.model.get('fulfillment').set('date_created', { $date: new Date().getTime() });
    },

    templateHelpers: function () {
        var assignee_id = this.model.get('assignee') && this.model.get('assignee').id;
        var submit_msg = _t('mobile_message_task_detials_error_not_assigned_to_you');

        if (assignee_id) {
            submit_msg = _t('mobile_message_task_detials_error_assigned_to', {
                user: this.model.get('assignee').get_display_name()
            });
        }

        let taskSource = null;
        if (this.model.get('source')) {
            if (this.model.get('source').get('device')) {
                taskSource = 'sensor';
            }

            if (this.model.get('source').get('form_submission')) {
                taskSource = this.model.get('source').get('form_submission').metadata.get('form_template_type');
            }
        }

        const show_completed_late =
            _.contains(['complete', 'complete_with_exception'], this.model.get('current_state')) &&
            this.model.get('is_completed_late');

        var helper_model = {
            can_complete_task: assignee_id == $f.active_user.id || assignee_id == null,
            form_template_title: this.model.get('reply').get('form_template')
                ? this.model.get('reply').get('form_template').get('title')
                : '',
            m_date_due:
                this.model.get('date_due') === undefined ? moment(new Date(this.model.get('date_due').$date)) : null,
            time_due: this.model.get_time_start_and_due_str(),
            submit_msg: submit_msg,
            is_fulfilled: this.model.is_fulfilled(),
            task_source: taskSource,
            show_completed_late
        };

        if (this.model.is_fulfilled()) {
            var quick_fulfill_images = [];
            if (this.model.get('fulfillment').get('type') === 'quick') {
                _.each(this.model.get('fulfillment').get('fields'), function (model, i) {
                    var images = [];
                    _.each(model.get('value'), function (image_location) {
                        images.push(image_location);
                    });
                    quick_fulfill_images.push({ notes: model.get('notes'), images: images, field_id: model.get('id') });
                });
            }

            $.extend(helper_model, {
                date_completed: this.model.get('fulfillment').get('date_submitted')
                    ? moment(new Date(this.model.get('fulfillment').get('date_submitted').$date)).format(
                          'ddd, MMM D, h:mm a'
                      )
                    : null,
                completed_by: this.model.get('fulfillment').get('user')
                    ? this.model.get('fulfillment').get('user').display_name
                    : '',
                quick_fulfill_images: quick_fulfill_images
            });
        }

        return helper_model;
    },

    events: {
        'click #submit_form': 'click_submit_task',
        'click .thumbnail': 'open_image_preview',
        'click .view_source_submission': function (event) {
            this.view_task_submission(event, true);
            return false;
        },
        'click .view_task_account': 'view_task_account',
        'click .task_fulfilled_info': function (event) {
            this.view_task_submission(event, false);
            return false;
        }
    },

    onRender: function () {
        var this_ = this;
        var fulfillment_fields = this.model.get('fulfillment').get('fields');
        var image_field = new MediaFieldTask(
            {
                id: 1,
                type: 'image',
                value: this_.model.get('title_image').toJSON()
            },
            {
                task_or_project: this.model
            },
            { silent: true }
        );

        if (this.model.get('reply').get('type') == 'quick') {
            this.r_task_image_field.show(
                new ImageFieldView({
                    task: this.model,
                    model: fulfillment_fields.length === 0 ? image_field : fulfillment_fields[0]
                })
            );
        }

        if (this.templateHelpers().is_fulfilled) {
            this.$el.find('textarea').attr('readonly', 'readonly');
            if (
                this.model.get('fulfillment').get('fields')[0] &&
                !this.model.get('fulfillment').get('fields')[0].get('notes')
            ) {
                this.$el.find('.notes_field').hide();
            }
        }

        if (this.model.get('cooldown') && $f.active_company.has_feature('sensor_cool_down')) {
            this._show_cooldown_stages();
        }

        if (this.model.get('source').get('triggering_image_fields')) {
            this.r_triggering_image_fields.show(
                new TriggeringImageFieldsList({
                    collection: this.model.get('source').get('triggering_image_fields')
                })
            );
        }

        // Create a title_images collection to hold stuff coming in from title_image and title_image_field
        var title_images = new Images();

        // get a TitleImageItem out of this?
        if (
            !this.model.get('title_image_field') &&
            this.model.get('title_image') &&
            this.model.get('title_image').length
        ) {
            title_images.add(this.model.get('title_image').models);
        }

        /* note: @author:halah @jira:MOBILE-767 this handles the half state we have when a user deleted an image
        on a task but view is displayed before the upload is finished, in that state title_images still has
        the deleted image but the image is removed on title_image_field's value which is why it is used here */
        if (this.model.get('title_image_field') && this.model.get('title_image_field').get('value')) {
            $.each(this.model.get('title_image_field').get('value'), function (i, file) {
                title_images.add(
                    new Image({
                        url: file
                    })
                );
            });
        }

        // Add image files coming in from title_image_field which might not have been uploaded yet
        if (this.model.get('title_image_field') && this.model.get('title_image_field').get('file')) {
            this.model
                .get('title_image_field')
                .get('file')
                .each(function (file) {
                    title_images.add(
                        new Image({
                            url: file.get_display_url(),
                            native_url: file.get_url_native()
                        })
                    );
                });
        }

        if (_.size(title_images) > 0) {
            this.r_title_images.show(new TitleImages({ collection: title_images }));
        }
    },

    /**
     * Fetch the account and view it.
     */
    view_task_account: function (event) {
        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        var account = new Account({ id: this.model.get('account').get('id') });
        showLoadingDialog();

        account.fetch({
            success: function () {
                hideLoadingDialog();
                import('./dashboard/views').then((module) => {
                    $f.mobile.nav.show(module.MobileDashboard, { active_account_or_group: account });
                });
            },
            error: function () {
                hideLoadingDialog();
                showErrorMessage(_t('mobile_message_task_detials_error_view_account'));
            }
        });

        return false;
    },

    view_task_submission: function (event, is_source) {
        if (!isConnected()) {
            showErrorMessage();
            return false;
        }

        var submission_id = null;

        if (is_source) {
            submission_id = this.model.get('source').get('form_submission')._id.$oid;
        } else {
            if (this.model.get('reply').get('type') !== 'form_submission') {
                return false; // there's no submission to show if this isn't a form task
            }

            submission_id = this.model.get('fulfillment').get('form_submission').id;
        }

        // @todo @author:david we should build this into FormStructure's fetch(), which would know to grab the template vs. submission
        showLoadingDialog();
        $f.mobile.ajax({
            url: '/api/v1/forms/get/' + submission_id + '?include_rules=true',
            dataType: 'json',
            type: 'POST',
            cache: false,
            success: async function (response) {
                hideLoadingDialog();
                var expanded_metadata = response.expanded_metadata;

                // We'll just delete the 'expanded_metadata key so
                // that we're not passing it to the FormStructure constructor
                delete response.expanded_metadata;

                const module = await import('../backbone/features/mobileForm/views');

                $f.mobile.nav.show(module.SubmittedFormPage, {
                    collection: new MobileFormStructure(response),
                    use_transitions: true,
                    expanded_metadata: expanded_metadata
                });
            },
            error: function () {
                hideLoadingDialog();
                showErrorMessage();
            }
        });

        return false;
    },

    click_submit_task: function (event) {
        var this_ = this;
        showLoadingDialog();
        // Get the device's location in the background so we have it cached for saving the task lat lon
        $f.mobile.get_device_location({
            success: function (position) {
                this_.model.set('lat', position.coords.lat);
                this_.model.set('lon', position.coords.lon);
                this_.model.get('fulfillment').set('lat', position.coords.lat);
                this_.model.get('fulfillment').set('lon', position.coords.lon);
            },
            error: function (error) {
                return;
            },
            complete: function () {
                hideLoadingDialog();
                this_._click_submit_task(event);
            }
        });
    },

    _click_submit_task: function (event) {
        var this_ = this;

        if ($(event.target).hasClass('disabled') || $(event.target).attr('disabled')) {
            return false;
        }

        if (this.model.is_missed()) {
            var date_due = this.model.get_date_due();
            var missed_message = _t('mobile_tasks_cannot_submit_missed');
            if (date_due) {
                var date_due_formatted = $f.utils.get_full_formatted_time(date_due);
                missed_message = _t('mobile_tasks_cannot_submit_missed_after', {
                    date: date_due_formatted
                });
            }
            showErrorMessage(missed_message);
            return;
        }

        if (!this.model.can_start()) {
            var upcoming_message = _t('mobile_tasks_cannot_submit_upcoming');
            showErrorMessage(upcoming_message);
            return;
        }

        if (this.model.get('current_state') === 'processing') {
            var processing_message = _t('mobile_tasks_cannot_submit_processing');
            showErrorMessage(processing_message);
            return;
        }

        if (this.model.get('reply').get('type') == 'form_submission') {
            // set this account, if there is one to pass over to the form
            var account = this_.model.get('account');
            $f.mobile.load_collection(
                this.model.get('reply').get('form_template').id,
                this_.model.get('id'),
                true,
                function (collection) {
                    var project = this_.model.get('project');
                    var account_fields = collection.where({ type: 'account' });

                    var smetadata_attrs = {
                        task: {
                            id: this_.model.get('id'),
                            title: this_.model.get('title'),
                            account_id: this_.model.get('account') && this_.model.get('account').id
                        }
                    };

                    if (project) {
                        smetadata_attrs.project = {
                            id: project.get('id'),
                            title: project.get('title')
                        };
                    }

                    collection.smetadata.set(smetadata_attrs);

                    if (!account || account_fields.length != 1) {
                        return;
                    }
                    account = account.toJSON();

                    // Prefill account
                    collection.get(account_fields.pop().id).set({
                        value: account.id,
                        account: account
                    });

                    /**
                     * @author:david unfortunately, the above .set()s on the collection will trigger a change
                     * and set the date_created, so that if the user clicks the back button without making any manual
                     * changes, they'll be asked if they want to save. This is for the best, though, as passing silent:true would
                     * a) not show the account in the account field, and b) allow the form to be reloaded and the task data wiped
                     * if the user were to close and open their app.
                     */
                }
            );

            return false;
        }

        if (
            this.model.get('reply').get('type') == 'quick' &&
            !this.model.get('fulfillment').get('fields')[0].isValid(true)
        ) {
            showErrorMessage(_t('mobile_message_task_detials_error_submit_task'));
            return false;
        }

        // Add task to $f.mobile.pending_task_submissions
        $f.mobile.pending_task_submissions.add_records(
            this.model,
            function () {
                this_.model.mark_as_complete_in_caches(function () {
                    $f.mobile.nav.go_back();

                    $f.mobile.uploads.start_upload({
                        msg: _t('mobile_message_task_detials_success_task_complete')
                    });

                    if ($f.flags.get_flag('use_local_notifications')) {
                        $f.mobile.notifications.cancelFutureLocalNotificationForTaskId(this_.model.get('id'));
                    }
                });
            },
            function () {
                showErrorMessage(_t('mobile_message_task_detials_error_submit_task_pending'));
            }
        );

        return false;
    },

    _show_cooldown_stages: async function () {
        const { CooldownStageListViewForTask } = await import('./sensors/views');
        this.r_cooldown_stages.show(
            new CooldownStageListViewForTask({
                collection: this.model.get('cooldown').get('stages')
            })
        );
    }
});

/**
 * Login and signup form.
 *
 * NOTE:  Please never instantiate this directly.  This should be
 * called through $f.mobile.logout function to make sure all settings
 * are cleared
 */
export const LoginForm = Backbone.Marionette.Layout.extend({
    template: '#tpl_login',
    active_locale: localization.DEFAULT_LOCALE,
    selected_form: null,
    show_password: false,
    email_prefill: null,
    password_prefill: null,
    login_as_prefill: null,

    regions: {
        r_language_selector: '.language_selector'
    },

    events: {
        'click .signup_btn': 'show_signup',
        'click .forgot_password_btn': 'forgot_password',
        'click .login_btn': 'show_login',
        'submit #signup_form': 'signup_submit',
        'submit #login_form': 'login_submit',
        'click .login_as_test': 'login_as_test',
        'click #login_continue': 'login_continue',
        'click .change_email_btn': 'change_email',
        "keypress input[name='email']": 'handle_continue_keypress'
    },

    templateHelpers: function () {
        const urlParams = new URLSearchParams(window.location.search);

        if ($f.flags.get_flag('clientside_invite_flow')) {
            const inviteCode = urlParams.get('invite');
            const inviteEmail = urlParams.get('invite_email');
            const inviteSms = urlParams.get('invite_sms');

            return {
                API_URL: API_URL,
                allow_debug_quick_login: this._allow_debug_quick_login(),
                show_password: this.show_password,
                email_prefill: this.email_prefill,
                password_prefill: this.password_prefill,
                login_as_prefill: this.login_as_prefill,
                version_info: $f.mobile.get_version_string(),
                isDebuggingEnabled: isDebuggingEnabled(),
                inviteCode,
                inviteEmail: $f.utils.validate_email(inviteEmail) && inviteEmail,
                inviteSms: $f.utils.validate_phone(inviteSms) && inviteSms
            };
        } else {
            return {
                API_URL: API_URL,
                allow_debug_quick_login: this._allow_debug_quick_login(),
                show_password: this.show_password,
                email_prefill: this.email_prefill,
                password_prefill: this.password_prefill,
                login_as_prefill: this.login_as_prefill,
                version_info: $f.mobile.get_version_string(),
                isDebuggingEnabled: isDebuggingEnabled()
            };
        }
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        hideLoadingDialog();
    },

    onShow: function () {
        this._set_status_bar_color();
    },

    onRender: function () {
        this._set_status_bar_color();

        var invite_code = $.get_url_var('invite');

        var this_ = this;

        this.r_language_selector.show(
            new LanguageSelector({
                active_locale: this_.active_locale,
                on_select: function (locale) {
                    this_.active_locale = locale;
                    $f.mobile.localization.set_locale(locale, true, function () {
                        this_.render();
                    });
                }
            })
        );

        if ($f.flags.get_flag('clientside_invite_flow')) {
            if (this.templateHelpers().inviteCode) {
                this.$el.find('#login_form').hide();
                this.show_signup();
            }
        } else {
            if (invite_code && invite_code.length > 4) {
                this.$el.find('input[name="invite_code"]').val(invite_code.trim().replace(/\//g, '')).hide();

                if ($f.user.invite.sms_number && $f.user.invite.sms_number.length > 0) {
                    this.$el.find('input[name="phone"]').val($f.user.invite.sms_number);
                }
                if ($f.user.invite.email && $f.user.invite.email.length > 0) {
                    this.$el.find('input[name="email"]').val($f.user.invite.email);
                }
                this.$el.find('#login_form').hide();
                this.show_signup();
            }
        }

        // offer to redirect to native app
        $f.mobile.is_oia_dialog_overdue(function (is_overdue) {
            if (!is_overdue) {
                return;
            }
            $f.mobile.nav.show_modal(OiaDialog, {});
        });

        // Need this flag to track which form we're rendering. This will go away once we separate login and signup into their own views.
        if (this.selected_form === 'login') {
            this.show_login();
        } else if (this.selected_form === 'signup') {
            this.show_signup();
        }

        var $password_field = this.$el.find('input[type=password]');
        if (this.show_password && !$password_field.val()) {
            // only focus pw input if it's empty (i.e., not prefilled by pw manager, etc.)
            $password_field.first().focus();
        }
    },

    forgot_password: function () {
        $f.mobile.open_uri(`${API_URL}/password_reset`);
        return false;
    },

    show_signup: function (event) {
        var this_ = this;
        this.$el.find('#login_form, #show_signup_btn_wrapper').fadeOut('fast', function () {
            this_.$el.find('.page_scrollable').scrollTop(0);
            this_.$el.find('#signup_form, #show_login_btn_wrapper').fadeIn('fast');
        });
        // This is ugly but as long as we keep these two in the same view we have to track them somehow.
        this.selected_form = 'signup';
        this.r_language_selector.currentView.render();
        return false;
    },

    show_login: function (event) {
        var this_ = this;
        this.$el.find('#signup_form, #show_login_btn_wrapper, #login_form .splash').fadeOut('fast', function () {
            this_.$el.find('.page_scrollable').scrollTop(0);
            this_.$el.find('#login_form .login, #login_form, #show_signup_btn_wrapper').fadeIn('fast');
        });
        // This is ugly but as long as we keep these two in the same view we have to track them somehow.
        this.selected_form = 'login';
        this.r_language_selector.currentView.render();
        return false;
    },

    login_as_test: function (event) {
        if (!this._allow_debug_quick_login()) {
            // HTML should not show if prod, but just to be safe
            return false;
        }

        this.$el.find('input[type="email"]').val('test+owner2@zenput.com');
        this.$el.find('input[type="password"]').val('zenput');
        return false;
    },

    signup_submit: function (event) {
        event.stopPropagation();
        event.preventDefault();
        var target = $(event.target);
        var view = this;

        var data = {
            invite_code: target.find('input[name="invite_code"]').val(),
            first_name: target.find('input[name="first_name"]').val(),
            last_name: target.find('input[name="last_name"]').val(),
            email: target.find('input[name="email"]').val(),
            phone: target.find('input[name="phone"]').val(),
            password: target.find('input[name="password"]').val(),
            password_confirm: target.find('input[name="password_confirm"]').val(),
            locale: view.active_locale // sends <lang>_<country>
        };

        showLoadingDialog();
        $f.mobile.ajax({
            url: '/api/v1/users/authorize_invite/',
            timeout: 15000,
            dataType: 'json',
            type: 'POST',
            cache: false,
            data: JSON.stringify(data),
            success: function (response) {
                $f.mobile.localization.set_locale(data.locale, true, function () {
                    if (isNative()) {
                        $f.mobile.login();
                        return;
                    }

                    try {
                        var user = response.results;
                        var is_manager = user.is_owner || user.is_admin || user.is_manager;
                        if (is_manager && isWeb()) {
                            window.location = '/home/';
                            return;
                        }
                    } catch (err) {
                        /* ignore, just got to regular mobile login */
                    }

                    $f.mobile.login();
                });
            },
            error: function (response) {
                hideLoadingDialog();
                showErrorMessage(response && response.message, null, 6000);
            }
        });
        return false;
    },

    login_continue: function (event) {
        var this_ = this;

        var $email_field = this.$el.find('input[name=email]');
        var $password_field = this.$el.find('input[type=password]');
        var $login_as_field = this.$el.find('input[name=login_as]');

        var email = $email_field.val();

        // if no email entered, or it doesn't pass HTML5 validation, show an error
        if (!email || ($email_field.get(0).checkValidity && !$email_field.get(0).checkValidity())) {
            return showErrorMessage(_t('mobile_login_no_email_error'));
        }
        this.$el.find('.oauth-login-error-sub-message').hide();

        getSSOCompanyInfo(email, function (response) {
            var oauth_company_info = response.message;
            if (response.success && oauth_company_info) {
                const { idp_entity_id, state } = oauth_company_info;
                this_.$el.find('.login').hide();
                this_.$el.find('.oauth-intermediary').show();
                if (isWeb()) {
                    const next_url = encodeURIComponent(`/api/v3/sso/process_login/mobile_web/?token=${state}`);
                    window.location.href = `/saml2/login/?idp=${idp_entity_id}&next=${next_url}`;
                    return;
                }
                setTimeout(function () {
                    authorizeDjangosaml2(idp_entity_id, state);
                }, 1500);
                return;
            }
            this_.show_password = true;
            this_.email_prefill = email;
            this_.password_prefill = $password_field.val() || null;
            this_.login_as_prefill = $login_as_field.val() || null;

            this_.render();
        });
    },

    handle_continue_keypress: function (e) {
        if (e.charCode != 13) {
            return;
        }
        this.login_continue();
    },

    change_email: function () {
        this.email_prefill = null;
        this.password_prefill = null;
        this.show_password = false;
        this.render();
    },

    login_submit: function (event) {
        var view = this;
        var target = $(event.target);

        if (!isConnected()) {
            showOfflineMessage();
            return false;
        }

        var data = {
            email: target.find('input[name="email"]').val(),
            password: target.find('input[name="password"]').val(),
            login_as: target.find('input[name="login_as"]').val(),
            locale: this.active_locale // This will send the entire string <lang>_<country>
        };

        if (data.email == 'test@zenput.com' && data.password == 'debug') {
            enableDebugging();
            this.$el.find('.logo').fadeOut(500).fadeIn(500).fadeOut(500).fadeIn(500);
            setTimeout(function () {
                view.render();
            }, 1000);
            return false;
        }

        showLoadingDialog();
        $f.mobile.ajax({
            url: '/api/v1/users/login/',
            dataType: 'json',
            type: 'POST',
            cache: false,
            data: JSON.stringify(data),
            timeout: 20000,
            success: $f.mobile.login,
            error: function (response) {
                response = response || {};
                hideLoadingDialog();

                showErrorMessage(response.message);
            }
        });

        return false;
    },

    _allow_debug_quick_login: () => ['local', 'development', 'dev0'].includes(process.env.DEPLOY_ENV),

    _set_status_bar_color: () => {
        // Use a darker color because cordova-plugin-statusbar won't change
        // the iOS text color correctly. We should revisit setting this to
        // the same background as the Login page once switching to Capacitor.
        setStatusBarColor('#191919');
    }
});

export const TasksLegendModal = Backbone.Marionette.Layout.extend({
    template: '#tpl_tasks_legend_modal',

    attributes: {
        id: 'tasks_legend_modal'
    },

    ANIMATION_TIME_MS: 500,

    title: null,
    description: null,

    _close_trigger_els: null, // used internally by _setup_close_events() and _destroy_close_events()

    templateHelpers: function () {
        return {
            title: this.title,
            description: this.description
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.title) {
            throw new Error('Programmer Error: TasksLegendModal must have a title');
        }
    },

    onRender: function () {
        this._setup_close_events();
    },

    onShow: function () {
        var this_ = this;

        // add backdrop when modal is opened and close modal when touching outside of it
        $f.mobile.nav.show_modal_touchblock({ duration: this_.ANIMATION_TIME_MS }, function () {
            this_.close();
            // fade the actual modal in too
            this_.$el.css('opacity', 0);
            this_.$el.velocity({ opacity: 1 }, { duration: this_.ANIMATION_TIME_MS / 2 });
        });
    },

    close: function () {
        var args_ = arguments;
        var this_ = this;

        this._destroy_close_events();

        $f.mobile.nav.hide_modal_touchblock({ duration: this_.ANIMATION_TIME_MS });
        // fade us out and then close
        this_.$el.velocity(
            { opacity: 0 },
            {
                duration: this.ANIMATION_TIME_MS / 2,
                complete: function () {
                    TasksLegendModal.__super__.close.apply(this_, args_);
                }
            }
        );
    },

    /**
     * Instead of using Bootstrap's method of closing on any data-dismiss=modal attribute or its .modal-backdrop,
     * let's handle the event ourselves through events and backbone. Instead of using this.events here,
     * we'll use .on() and .off() so our subclasses don't have to worry about extending this.events.
     */
    _setup_close_events: function () {
        var this_ = this;
        this._close_trigger_els = this.$el.find('.modal_footer .close_modal');

        // remove bootstrap's default click behavior for backdrop as well as any events we already set up
        this._destroy_close_events();

        // set up the close button and backdrop to call close()
        this._close_trigger_els.on('click', function (event) {
            this_.close(event);
        });
    },

    _destroy_close_events: function () {
        this._close_trigger_els.off('click');
    }
});

export const StatusPage = BaseToolbarNavPage.extend({
    template: '#tpl_company_status_page',
    inactive_display: false,
    title: '',
    message: '',
    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        // calling this preemptively here because some views call show on this and fail to hide it
        hideLoadingDialog();
        if (this.messaging) {
            this.title = this.messaging.title;
            this.message = this.messaging.message;
        }
    },
    templateHelpers: function () {
        return {
            title: this.title,
            message: this._get_display_message()
        };
    },
    _get_display_message: function () {
        var message = this.message;
        var re = /(\w+@zenput\.com)/g;
        return message.replace(re, '<a href="mailto:$1">$1</a>');
    },
    show_back_button: function () {
        return false;
    },
    toolbarnav_title: '',
    onRender: function () {
        if (this.inactive_display) {
            return;
        }
        $f.active_company.fetch({
            force: true,
            success: function (model) {
                // log them out without redirecting to LoginForm immediately
                if (model.is_subscription_inactive()) {
                    $f.mobile.logout(false);
                    return;
                }
                if (!model.is_subscription_valid()) {
                    return;
                }
                import('./dashboard/views').then((module) => {
                    $f.mobile.nav.show(module.MobileDashboard, { is_top_level_page: true });
                });
            }
        });
    }
});
